/*
 * BioIndex
 * Copyright (c) 2026 BioIndex™. All rights reserved.
 * Unauthorized copying, scraping, redistribution, or commercial reuse is prohibited.
 */

import { useEffect, useMemo, useRef, useState } from "react";
import { doc, onSnapshot } from "firebase/firestore";
import { getFunctions, httpsCallable } from "firebase/functions";
import { auth, db } from "../firebase";
import {
  classesByPhylum,
  familiesByOrder,
  generaByFamily,
  ordersByClass,
} from "../utils/taxonomy";

const FLORAL_PART_COUNT_OPTIONS = [
  ["one", "1"],
  ["two", "2"],
  ["three", "3"],
  ["four", "4"],
  ["five", "5"],
  ["six", "6"],
  ["seven", "7"],
  ["eight", "8"],
  ["nine", "9"],
  ["ten", "10"],
  ["eleven", "11"],
  ["twelve", "12"],
  ["many", "Numerous"],
];

const LEAF_COLOR_OPTIONS = [
  ["dark-green", "Dark green"],
  ["green", "Green"],
  ["light-green", "Light green"],
  ["yellow-green", "Yellow-green"],
  ["blue-green", "Blue-green"],
  ["gray-green", "Gray-green"],
  ["silver", "Silvery"],
  ["gray", "Gray"],
  ["white", "White"],
  ["red", "Red"],
  ["purple", "Purple"],
  ["bronze", "Bronze"],
  ["brown", "Brown"],
];

const ANGIOSPERM_REPRODUCTIVE_TRAIT_KEYS = new Set([
  "flowerPresentation",
  "inflorescenceType",
  "flowerSymmetry",
  "flowerSexuality",
  "unisexualSystem",
  "mixedSexualSystem",
  "perianthType",
  "sepalNumber",
  "petalNumber",
  "tepalNumber",
  "flowerColor",
  "ovaryPosition",
  "fruitClass",
  "fruitTexture",
  "fruitDehiscence",
  "fruitColor",
]);

const GYMNOSPERM_REPRODUCTIVE_TRAIT_KEYS = new Set([
  "gymnospermSexualSystem",
  "pollenStructure",
  "ovuleStructure",
  "seedCovering",
  "seedColor",
]);

const TAXONOMY_TRAIT_INHERITANCE = {
  annonaceae: {
    sourceRank: "Family",
    sourceName: "Annonaceae",
    traits: {
      leafArrangement: "alternate",
      leafComplexity: "simple",
      leafMargin: "entire",
      venation: "pinnate",
      stipules: "absent",
      flowerSymmetry: "radial",
      ovaryPosition: "superior",
    },
  },
  araceae: {
    sourceRank: "Family",
    sourceName: "Araceae",
    traits: {
      growthForm: ["herb"],
      leafArrangement: "alternate",
    },
  },
  arecaceae: {
    sourceRank: "Family",
    sourceName: "Arecaceae",
    traits: {
      growthForm: ["palm"],
      ovaryPosition: "superior",
    },
  },
  asteraceae: {
    sourceRank: "Family",
    sourceName: "Asteraceae",
    traits: {
      stipules: "absent",
      flowerPresentation: "inflorescence",
      inflorescenceType: "head",
      ovaryPosition: "inferior",
      fruitDehiscence: "indehiscent",
    },
  },
  brassicaceae: {
    sourceRank: "Family",
    sourceName: "Brassicaceae",
    traits: {
      stipules: "absent",
      ovaryPosition: "superior",
    },
  },
  araucariaceae: {
    sourceRank: "Family",
    sourceName: "Araucariaceae",
    traits: {
      leafComplexity: "simple",
      pollenStructure: "pollen-cone",
      ovuleStructure: "seed-cone",
    },
  },
  cycadaceae: {
    sourceRank: "Family",
    sourceName: "Cycadaceae",
    traits: {
      gymnospermSexualSystem: "dioecious",
      pollenStructure: "pollen-cone",
      ovuleStructure: "megasporophylls",
    },
  },
  cupressaceae: {
    sourceRank: "Family",
    sourceName: "Cupressaceae",
    traits: {
      leafComplexity: "simple",
      pollenStructure: "pollen-cone",
      ovuleStructure: "seed-cone",
    },
  },
  cyperaceae: {
    sourceRank: "Family",
    sourceName: "Cyperaceae",
    traits: {
      growthForm: ["sedge"],
      ovaryPosition: "superior",
      fruitTexture: "dry",
      fruitDehiscence: "indehiscent",
    },
  },
  fagaceae: {
    sourceRank: "Family",
    sourceName: "Fagaceae",
    traits: {
      leafComplexity: "simple",
      venation: "pinnate",
      stipules: "caducous",
      flowerSexuality: "unisexual",
      unisexualSystem: "monoecious",
      ovaryPosition: "inferior",
      fruitClass: "nut",
      fruitTexture: "dry",
      fruitDehiscence: "indehiscent",
    },
  },
  juncaceae: {
    sourceRank: "Family",
    sourceName: "Juncaceae",
    traits: {
      flowerSymmetry: "radial",
      perianthType: "tepals",
      ovaryPosition: "superior",
      fruitClass: "capsule",
      fruitTexture: "dry",
      fruitDehiscence: "dehiscent",
    },
  },
  lacistemataceae: {
    sourceRank: "Family",
    sourceName: "Lacistemataceae",
    traits: {
      leafArrangement: "alternate",
      leafComplexity: "simple",
      perianthType: "sepals-only",
      ovaryPosition: "superior",
      fruitDehiscence: "dehiscent",
    },
  },
  lauraceae: {
    sourceRank: "Family",
    sourceName: "Lauraceae",
    traits: {
      leafComplexity: "simple",
      stipules: "absent",
      flowerSymmetry: "radial",
      perianthType: "tepals",
      ovaryPosition: "superior",
    },
  },
  moraceae: {
    sourceRank: "Family",
    sourceName: "Moraceae",
    traits: {
      leafComplexity: "simple",
      exudate: "white-latex",
      flowerSexuality: "unisexual",
    },
  },
  orchidaceae: {
    sourceRank: "Family",
    sourceName: "Orchidaceae",
    traits: {
      stipules: "absent",
      perianthType: "sepals-petals",
      sepalNumber: ["three"],
      petalNumber: ["three"],
      ovaryPosition: "inferior",
    },
  },
  pinaceae: {
    sourceRank: "Family",
    sourceName: "Pinaceae",
    traits: {
      leafComplexity: "simple",
      pollenStructure: "pollen-cone",
      ovuleStructure: "seed-cone",
    },
  },
  podocarpaceae: {
    sourceRank: "Family",
    sourceName: "Podocarpaceae",
    traits: {
      pollenStructure: "pollen-cone",
      ovuleStructure: "reduced-cone",
    },
  },
  poaceae: {
    sourceRank: "Family",
    sourceName: "Poaceae",
    traits: {
      growthForm: ["grass"],
      leafArrangement: "alternate",
      leafComplexity: "simple",
      venation: "parallel",
      ovaryPosition: "superior",
    },
  },
  rubiaceae: {
    sourceRank: "Family",
    sourceName: "Rubiaceae",
    traits: {
      leafComplexity: "simple",
    },
  },
  sapotaceae: {
    sourceRank: "Family",
    sourceName: "Sapotaceae",
    traits: {
      leafComplexity: "simple",
      leafMargin: "entire",
      venation: "pinnate",
      ovaryPosition: "superior",
    },
  },
  taxaceae: {
    sourceRank: "Family",
    sourceName: "Taxaceae",
    traits: {
      pollenStructure: "pollen-cone",
      ovuleStructure: "solitary-ovule",
    },
  },
  zamiaceae: {
    sourceRank: "Family",
    sourceName: "Zamiaceae",
    traits: {
      gymnospermSexualSystem: "dioecious",
      pollenStructure: "pollen-cone",
      ovuleStructure: "seed-cone",
    },
  },
  solanaceae: {
    sourceRank: "Family",
    sourceName: "Solanaceae",
    traits: {
      stipules: "absent",
      perianthType: "sepals-petals",
    },
  },
};

const TAXONOMY_GENUS_TRAIT_INHERITANCE = {
  acer: {
    sourceRank: "Genus",
    sourceName: "Acer",
    traits: {
      leafArrangement: "opposite",
      flowerSymmetry: "radial",
      fruitClass: "samara",
      fruitTexture: "dry",
      fruitDehiscence: "indehiscent",
    },
  },
  aegiphila: {
    sourceRank: "Genus",
    sourceName: "Aegiphila",
    traits: {
      leafComplexity: "simple",
      flowerSymmetry: "radial",
      perianthType: "sepals-petals",
      ovaryPosition: "superior",
      fruitClass: "drupe",
      fruitDehiscence: "indehiscent",
    },
  },
  annona: {
    sourceRank: "Genus",
    sourceName: "Annona",
    traits: {
      perianthType: "sepals-petals",
      sepalNumber: ["three"],
      fruitClass: "aggregate",
      fruitTexture: "fleshy",
      fruitDehiscence: "indehiscent",
    },
  },
  aphananthe: {
    sourceRank: "Genus",
    sourceName: "Aphananthe",
    traits: {
      leafArrangement: "alternate",
      leafComplexity: "simple",
      stipules: "caducous",
      flowerSexuality: "unisexual",
      perianthType: "tepals",
      fruitClass: "drupe",
      fruitDehiscence: "indehiscent",
    },
  },
  astrocaryum: {
    sourceRank: "Genus",
    sourceName: "Astrocaryum",
    traits: {
      leafComplexity: "compound",
      flowerSexuality: "unisexual",
      unisexualSystem: "monoecious",
      perianthType: "sepals-petals",
      sepalNumber: ["three"],
      petalNumber: ["three"],
    },
  },
  bactris: {
    sourceRank: "Genus",
    sourceName: "Bactris",
    traits: {
      flowerSexuality: "unisexual",
      unisexualSystem: "monoecious",
      perianthType: "sepals-petals",
      sepalNumber: ["three"],
      petalNumber: ["three"],
      fruitClass: "drupe",
      fruitDehiscence: "indehiscent",
    },
  },
  blumea: {
    sourceRank: "Genus",
    sourceName: "Blumea",
    traits: {
      leafArrangement: "alternate",
      leafComplexity: "simple",
      flowerSexuality: "mixed",
      mixedSexualSystem: "gynomonoecious",
      fruitClass: "achene",
      fruitTexture: "dry",
    },
  },
  capparidastrum: {
    sourceRank: "Genus",
    sourceName: "Capparidastrum",
    traits: {
      leafComplexity: "simple",
      flowerPresentation: "inflorescence",
      inflorescenceType: "raceme",
      flowerSymmetry: "radial",
      flowerSexuality: "perfect",
      perianthType: "sepals-petals",
      sepalNumber: ["four"],
      petalNumber: ["four"],
    },
  },
  chamaedorea: {
    sourceRank: "Genus",
    sourceName: "Chamaedorea",
    traits: {
      armature: "absent",
      flowerSexuality: "unisexual",
      unisexualSystem: "dioecious",
      perianthType: "sepals-petals",
      sepalNumber: ["three"],
      petalNumber: ["three"],
      fruitClass: "drupe",
      fruitDehiscence: "indehiscent",
    },
  },
  coccoloba: {
    sourceRank: "Genus",
    sourceName: "Coccoloba",
    traits: {
      leafArrangement: "alternate",
      leafComplexity: "simple",
      stipules: "ochreate",
      flowerPresentation: "inflorescence",
      perianthType: "tepals",
      tepalNumber: ["five"],
      ovaryPosition: "superior",
      fruitClass: "achene",
      fruitTexture: "dry",
      fruitDehiscence: "indehiscent",
    },
  },
  dendropanax: {
    sourceRank: "Genus",
    sourceName: "Dendropanax",
    traits: {
      leafComplexity: "simple",
      flowerPresentation: "inflorescence",
      ovaryPosition: "inferior",
      fruitClass: "drupe",
      fruitDehiscence: "indehiscent",
    },
  },
  diospyros: {
    sourceRank: "Genus",
    sourceName: "Diospyros",
    traits: {
      leafArrangement: "alternate",
      leafComplexity: "simple",
      leafMargin: "entire",
      stipules: "absent",
      flowerSymmetry: "radial",
      perianthType: "sepals-petals",
      ovaryPosition: "superior",
    },
  },
  espeletia: {
    sourceRank: "Genus",
    sourceName: "Espeletia",
    traits: {
      leafArrangement: "alternate",
      flowerSexuality: "unisexual",
      unisexualSystem: "monoecious",
      fruitClass: "achene",
      fruitTexture: "dry",
    },
  },
  ginkgo: {
    sourceRank: "Genus",
    sourceName: "Ginkgo",
    traits: {
      growthForm: ["tree"],
      leafPersistence: "deciduous",
      leafArrangement: ["alternate", "fascicled"],
      leafComplexity: "simple",
      leafShape: ["flabellate"],
      venation: "dichotomous",
      stipules: "absent",
      gymnospermSexualSystem: "dioecious",
      pollenStructure: "catkin-like-strobilus",
      ovuleStructure: "paired-ovules-on-stalk",
      seedCovering: "fleshy-outer-coat",
    },
  },
  gliricidia: {
    sourceRank: "Genus",
    sourceName: "Gliricidia",
    traits: {
      leafComplexity: "compound",
      compoundType: "imparipinnate",
      flowerPresentation: "inflorescence",
      inflorescenceType: "raceme",
      perianthType: "sepals-petals",
      fruitClass: "legume",
      fruitTexture: "dry",
      fruitDehiscence: "dehiscent",
    },
  },
  guarea: {
    sourceRank: "Genus",
    sourceName: "Guarea",
    traits: {
      leafComplexity: "compound",
      flowerPresentation: "inflorescence",
      flowerSexuality: "unisexual",
      unisexualSystem: "dioecious",
      perianthType: "sepals-petals",
      ovaryPosition: "superior",
      fruitClass: "capsule",
      fruitTexture: "dry",
      fruitDehiscence: "dehiscent",
    },
  },
  helianthus: {
    sourceRank: "Genus",
    sourceName: "Helianthus",
    traits: {
      growthForm: ["herb"],
      leafComplexity: "simple",
      fruitClass: "achene",
      fruitTexture: "dry",
    },
  },
  koanophyllon: {
    sourceRank: "Genus",
    sourceName: "Koanophyllon",
    traits: {
      leafComplexity: "simple",
      fruitClass: "achene",
      fruitTexture: "dry",
    },
  },
  lacistema: {
    sourceRank: "Genus",
    sourceName: "Lacistema",
    traits: {
      flowerPresentation: "inflorescence",
      inflorescenceType: "spike",
    },
  },
  lantana: {
    sourceRank: "Genus",
    sourceName: "Lantana",
    traits: {
      leafComplexity: "simple",
      flowerPresentation: "inflorescence",
      perianthType: "sepals-petals",
      fruitClass: "drupe",
      fruitDehiscence: "indehiscent",
    },
  },
  licaria: {
    sourceRank: "Genus",
    sourceName: "Licaria",
    traits: {
      venation: "pinnate",
      flowerPresentation: "inflorescence",
      flowerSexuality: "perfect",
      tepalNumber: ["six"],
    },
  },
  miconia: {
    sourceRank: "Genus",
    sourceName: "Miconia",
    traits: {
      fruitClass: "berry",
      fruitTexture: "fleshy",
      fruitDehiscence: "indehiscent",
    },
  },
  omphalea: {
    sourceRank: "Genus",
    sourceName: "Omphalea",
    traits: {
      leafArrangement: "alternate",
      leafComplexity: "simple",
      flowerPresentation: "inflorescence",
      flowerSexuality: "unisexual",
      unisexualSystem: "monoecious",
      perianthType: "sepals-only",
    },
  },
  parathesis: {
    sourceRank: "Genus",
    sourceName: "Parathesis",
    traits: {
      leafArrangement: "alternate",
      flowerPresentation: "inflorescence",
      flowerSexuality: "perfect",
      perianthType: "sepals-petals",
    },
  },
  piper: {
    sourceRank: "Genus",
    sourceName: "Piper",
    traits: {
      leafArrangement: "alternate",
      leafComplexity: "simple",
      flowerPresentation: "inflorescence",
      perianthType: "absent",
      ovaryPosition: "superior",
    },
  },
  posoqueria: {
    sourceRank: "Genus",
    sourceName: "Posoqueria",
    traits: {
      stipules: "interpetiolar",
      flowerPresentation: "inflorescence",
      flowerSymmetry: "bilateral",
      flowerSexuality: "perfect",
      perianthType: "sepals-petals",
      sepalNumber: ["five"],
      petalNumber: ["five"],
    },
  },
  pseudolmedia: {
    sourceRank: "Genus",
    sourceName: "Pseudolmedia",
    traits: {
      growthForm: ["tree"],
      stipules: "caducous",
      unisexualSystem: "dioecious",
    },
  },
  pterocarpus: {
    sourceRank: "Genus",
    sourceName: "Pterocarpus",
    traits: {
      growthForm: ["tree"],
      leafArrangement: "alternate",
      leafComplexity: "compound",
      flowerPresentation: "inflorescence",
      perianthType: "sepals-petals",
      fruitTexture: "dry",
      fruitDehiscence: "indehiscent",
    },
  },
  quararibea: {
    sourceRank: "Genus",
    sourceName: "Quararibea",
    traits: {
      leafComplexity: "simple",
      stipules: "caducous",
      flowerSymmetry: "radial",
      flowerSexuality: "perfect",
      perianthType: "sepals-petals",
      petalNumber: ["five"],
      ovaryPosition: "half-inferior",
      fruitDehiscence: "indehiscent",
    },
  },
  quercus: {
    sourceRank: "Genus",
    sourceName: "Quercus",
    traits: {
      leafArrangement: "alternate",
    },
  },
  reinhardtia: {
    sourceRank: "Genus",
    sourceName: "Reinhardtia",
    traits: {
      armature: "absent",
      flowerSexuality: "unisexual",
      unisexualSystem: "monoecious",
      perianthType: "sepals-petals",
      sepalNumber: ["three"],
      petalNumber: ["three"],
    },
  },
  sapium: {
    sourceRank: "Genus",
    sourceName: "Sapium",
    traits: {
      leafComplexity: "simple",
      venation: "pinnate",
      flowerSexuality: "unisexual",
      perianthType: "sepals-only",
    },
  },
  senna: {
    sourceRank: "Genus",
    sourceName: "Senna",
    traits: {
      leafComplexity: "compound",
      compoundType: "paripinnate",
      perianthType: "sepals-petals",
      sepalNumber: ["five"],
      petalNumber: ["five"],
      fruitClass: "legume",
    },
  },
  sideroxylon: {
    sourceRank: "Genus",
    sourceName: "Sideroxylon",
    traits: {
      fruitClass: "berry",
      fruitTexture: "fleshy",
      fruitDehiscence: "indehiscent",
    },
  },
  swartzia: {
    sourceRank: "Genus",
    sourceName: "Swartzia",
    traits: {
      armature: "absent",
      leafArrangement: "alternate",
      leafComplexity: "compound",
      flowerSexuality: "perfect",
      fruitClass: "legume",
      fruitTexture: "dry",
    },
  },
  trichilia: {
    sourceRank: "Genus",
    sourceName: "Trichilia",
    traits: {
      leafArrangement: "alternate",
      leafComplexity: "compound",
      flowerPresentation: "inflorescence",
      perianthType: "sepals-petals",
      fruitClass: "capsule",
      fruitTexture: "dry",
      fruitDehiscence: "dehiscent",
    },
  },
  trophis: {
    sourceRank: "Genus",
    sourceName: "Trophis",
    traits: {
      armature: "absent",
      leafArrangement: "alternate",
      stipules: "free-lateral",
      unisexualSystem: "dioecious",
      perianthType: "tepals",
      tepalNumber: ["four"],
      fruitDehiscence: "indehiscent",
    },
  },
  vachellia: {
    sourceRank: "Genus",
    sourceName: "Vachellia",
    traits: {
      leafComplexity: "compound",
      compoundType: "bipinnate",
      perianthType: "sepals-petals",
      fruitClass: "legume",
    },
  },
};

const EMPTY_TRAITS = Object.freeze({});
const FLORAL_PART_COUNT_KEYS = new Set([
  "sepalNumber",
  "petalNumber",
  "tepalNumber",
]);

function findTaxonomyParent(groups, childName) {
  const childKey = String(childName || "")
    .trim()
    .toLowerCase();

  if (!childKey) return "";

  for (const [parentName, children] of Object.entries(groups || {})) {
    const containsChild = (Array.isArray(children) ? children : []).some(
      (candidate) =>
        String(candidate || "")
          .trim()
          .toLowerCase() === childKey,
    );

    if (containsChild) return String(parentName || "").trim().toLowerCase();
  }

  return "";
}

function getTaxonomyGenusKey(scientificName) {
  return String(scientificName || "")
    .trim()
    .split(/\s+/)[0]
    .toLowerCase();
}

function getTaxonomyFamilyKey(scientificName) {
  return findTaxonomyParent(
    generaByFamily,
    getTaxonomyGenusKey(scientificName),
  );
}

function getTaxonomyPhylumKey(scientificName) {
  const family = getTaxonomyFamilyKey(scientificName);
  const order = findTaxonomyParent(familiesByOrder, family);
  const className = findTaxonomyParent(ordersByClass, order);

  return findTaxonomyParent(classesByPhylum, className);
}

function getPlantReproductiveGroup(scientificName) {
  const phylum = getTaxonomyPhylumKey(scientificName);

  if (phylum === "magnoliophyta") return "angiosperm";

  if (["cycadophyta", "ginkgophyta", "pinophyta"].includes(phylum)) {
    return "gymnosperm";
  }

  if (
    [
      "anthocerotophyta",
      "bryophyta",
      "lycophyta",
      "marchantiophyta",
      "pteridophyta",
    ].includes(phylum)
  ) {
    return "spore-bearing";
  }

  return "";
}

function getTaxonomyTraitInheritance(scientificName) {
  const familyRule =
    TAXONOMY_TRAIT_INHERITANCE[getTaxonomyFamilyKey(scientificName)];
  const genusRule =
    TAXONOMY_GENUS_TRAIT_INHERITANCE[getTaxonomyGenusKey(scientificName)];
  const sources = [familyRule, genusRule].filter(Boolean);

  if (sources.length === 0) return null;

  const traits = {};
  const traitSources = {};

  sources.forEach((source) => {
    Object.entries(source.traits).forEach(([key, value]) => {
      traits[key] = value;
      traitSources[key] = {
        sourceRank: source.sourceRank,
        sourceName: source.sourceName,
      };
    });
  });

  return {
    sourceRank: sources.length === 1 ? sources[0].sourceRank : "Taxonomy",
    sourceName: sources.map((source) => source.sourceName).join(" and "),
    sources,
    traits,
    traitSources,
  };
}

function hasOwnTrait(traits, key) {
  return Object.prototype.hasOwnProperty.call(traits || {}, key);
}

function removeInheritedTraits(traits, inheritedTraits) {
  return Object.fromEntries(
    Object.entries(traits || {}).filter(
      ([key]) => !hasOwnTrait(inheritedTraits, key),
    ),
  );
}

const DESCRIPTION_SECTIONS = [
  {
    id: "habit",
    label: "Habit",
    shortLabel: "Habit",
    fields: [
      {
        key: "growthForm",
        label: "Growth form",
        hint: "Select up to three growth forms that reliably apply to this species.",
        maxSelections: 3,
        options: [
          ["tree", "Tree"],
          ["shrub", "Shrub"],
          ["herb", "Herb"],
          ["vine", "Vine"],
          ["liana", "Liana"],
          ["epiphyte", "Epiphyte"],
          ["palm", "Palm"],
          ["grass", "Grass"],
          ["sedge", "Sedge"],
          ["fern", "Fern"],
          ["aquatic", "Aquatic"],
        ],
      },
      {
        key: "stemHabit",
        label: "Stem habit",
        hint: "How the stems are held or spread.",
        options: [
          ["erect", "Erect"],
          ["climbing", "Climbing"],
          ["creeping", "Creeping"],
          ["trailing", "Trailing"],
          ["rosette", "Rosette-forming"],
          ["tufted", "Tufted"],
        ],
      },
      {
        key: "armature",
        label: "Spines, thorns, or prickles",
        hint: "Defensive structures visible on the plant.",
        options: [
          ["absent", "No defensive structures"],
          ["spines", "Spines"],
          ["thorns", "Thorns"],
          ["prickles", "Prickles"],
        ],
      },
    ],
  },
  {
    id: "leaves",
    label: "Leaves",
    shortLabel: "Leaves",
    fields: [
      {
        key: "leafPersistence",
        label: "Leaf persistence",
        hint: "Whether foliage remains through the year.",
        options: [
          ["evergreen", "Evergreen"],
          ["deciduous", "Deciduous"],
          ["seasonal", "Seasonally leafless"],
        ],
      },
      {
        key: "leafArrangement",
        label: "Leaf arrangement",
        hint: "Select one typical arrangement, or up to two when both reliably occur on different shoot types.",
        maxSelections: 2,
        options: [
          ["alternate", "Alternate"],
          ["opposite", "Opposite"],
          ["whorled", "Whorled"],
          ["basal", "Basal"],
          ["rosette", "Rosette"],
          ["fascicled", "Fascicled"],
        ],
      },
      {
        key: "leafComplexity",
        label: "Leaf complexity",
        hint: "Whether each leaf has one blade or several leaflets.",
        options: [
          ["simple", "Simple"],
          ["compound", "Compound"],
        ],
      },
      {
        key: "compoundType",
        label: "Compound leaf type",
        hint: "The arrangement of the leaflets.",
        showWhen: (traits) => traits.leafComplexity === "compound",
        options: [
          ["unifoliolate", "Unifoliolate"],
          ["bifoliate", "Bifoliate"],
          ["trifoliate", "Trifoliate"],
          ["imparipinnate", "Imparipinnate"],
          ["paripinnate", "Paripinnate"],
          ["bipinnate", "Bipinnately compound"],
          ["tripinnate", "Tripinnately compound"],
          ["palmate", "Palmately compound"],
        ],
      },

      {
        key: "leafShape",
        label: "Leaf or leaflet shape",
        hint: "Select up to three shapes, in order from most typical or common to less typical.",
        maxSelections: 3,
        options: [
          ["elliptic", "Elliptic"],
          ["ovate", "Ovate"],
          ["obovate", "Obovate"],
          ["oblong", "Oblong"],
          ["lanceolate", "Lanceolate"],
          ["oblanceolate", "Oblanceolate"],
          ["linear", "Linear"],
          ["acicular", "Acicular"],
          ["spatulate", "Spatulate"],
          ["cordate", "Cordate"],
          ["reniform", "Reniform"],
          ["orbicular", "Orbicular"],
          ["deltoid", "Deltoid"],
          ["rhombic", "Rhombic"],
          ["sagittate", "Sagittate"],
          ["hastate", "Hastate"],
          ["flabellate", "Flabellate (fan-shaped)"],
        ],
      },
      {
        key: "leafMargin",
        label: "Leaf margin",
        hint: "The form of the leaf edge.",
        options: [
          ["entire", "Entire"],
          ["serrate", "Serrate"],
          ["dentate", "Dentate"],
          ["crenate", "Crenate"],
          ["lobed", "Lobed"],
          ["undulate", "Undulate"],
          ["spinose", "Spinose"],
        ],
      },
      {
        key: "leafApex",
        label: "Leaf apex",
        hint: "Select one typical apex, or up to two when both reliably occur.",
        maxSelections: 2,
        options: [
          ["acute", "Acute"],
          ["acuminate", "Acuminate"],
          ["obtuse", "Obtuse"],
          ["rounded", "Rounded"],
          ["emarginate", "Emarginate"],
          ["mucronate", "Mucronate"],
        ],
      },
      {
        key: "leafBase",
        label: "Leaf base",
        hint: "Select one typical base, or up to two when both reliably occur.",
        maxSelections: 2,
        options: [
          ["cuneate", "Cuneate"],
          ["obtuse", "Obtuse"],
          ["rounded", "Rounded"],
          ["cordate", "Cordate"],
          ["truncate", "Truncate"],
          ["oblique", "Oblique"],
          ["attenuate", "Attenuate"],
        ],
      },
      {
        key: "leafTexture",
        label: "Leaf texture",
        hint: "The thickness and feel of the blade.",
        icon: false,
        options: [
          ["membranous", "Membranous"],
          ["chartaceous", "Chartaceous"],
          ["coriaceous", "Coriaceous"],
          ["succulent", "Succulent"],
        ],
      },
      {
        key: "venation",
        label: "Venation",
        hint: "The main vein pattern.",
        options: [
          ["pinnate", "Pinnate venation"],
          ["palmate", "Palmate venation"],
          ["parallel", "Parallel venation"],
          ["arcuate", "Arcuate venation"],
          ["dichotomous", "Dichotomous venation"],
          ["triplinerved", "Triplinerved venation"],
        ],
      },
      {
        key: "stipules",
        label: "Stipules",
        hint: "The position or form of appendages at the leaf base.",
        icon: false,
        options: [
          ["absent", "No stipules"],
          ["free-lateral", "Free lateral"],
          ["interpetiolar", "Interpetiolar"],
          ["intrapetiolar", "Intrapetiolar"],
          ["adnate", "Adnate to petiole"],
          ["ochreate", "Ochreate or sheathing"],
          ["foliaceous", "Foliaceous"],
          ["spinescent", "Spinescent"],
          ["caducous", "Caducous"],
          ["inconspicuous", "Inconspicuous"],
        ],
      },
      {
        key: "petioleLength",
        label: "Leaf attachment and petiole length",
        hint: "Petiole length on a typical mature leaf.",
        options: [
          ["sessile", "Sessile"],
          ["subsessile", "Subsessile (<0.5 cm)"],
          ["short", "Short (0.5–2 cm)"],
          ["medium", "Medium (2–5 cm)"],
          ["long", "Long (>5 cm)"],
        ],
      },
      {
        key: "petioleForm",
        label: "Petiole form",
        hint: "The cross-section or conspicuous structure of the petiole.",
        icon: false,
        options: [
          ["terete", "Terete"],
          ["canaliculate", "Canaliculate"],
          ["flattened", "Flattened"],
          ["winged", "Winged"],
          ["pulvinate", "Pulvinate"],
        ],
      },
      {
        key: "petiolePulvinusPosition",
        label: "Pulvinus position",
        hint: "Where the conspicuous swelling occurs along the petiole.",
        icon: false,
        showWhen: (traits) => traits.petioleForm === "pulvinate",
        options: [
          ["basal", "Basal"],
          ["apical", "Apical"],
          ["both", "Basal and apical"],
        ],
      },
      {
        key: "petioleGlands",
        label: "Petiole glands",
        hint: "Whether glands occur on the petiole and where they are concentrated.",
        icon: false,
        options: [
          ["absent", "No petiole glands"],
          ["apical", "Near the blade"],
          ["basal", "Near the stem"],
          ["medial", "Along the petiole"],
          ["multiple", "At multiple positions"],
        ],
      },
      {
        key: "exudate",
        label: "Exudate",
        hint: "Sap or latex visible when tissue is damaged.",
        options: [
          ["absent", "Absent"],
          ["clear", "Clear sap"],
          ["white-latex", "White latex"],
          ["yellow-latex", "Yellow latex"],
          ["orange-latex", "Orange latex"],
          ["red-latex", "Red latex"],
          ["other-colored-latex", "Other colored latex"],
        ],
      },
    ],
  },
  {
    id: "leafSurfaces",
    label: "Leaf surfaces",
    shortLabel: "Surfaces",
    fields: [
      {
        key: "upperLeafColor",
        label: "Upper surface color (adaxial)",
        hint: "Typical color of the upper surface of a mature, healthy leaf. Select up to two when the species reliably varies.",
        maxSelections: 2,
        options: LEAF_COLOR_OPTIONS,
      },
      {
        key: "lowerLeafColor",
        label: "Lower surface color (abaxial)",
        hint: "Typical color of the lower surface of a mature, healthy leaf. Select up to two when the species reliably varies.",
        copyFrom: "upperLeafColor",
        copyLabel: "Same as upper",
        maxSelections: 2,
        options: LEAF_COLOR_OPTIONS,
      },
      {
        key: "upperLeafSurface",
        label: "Upper surface traits (adaxial)",
        hint: "Hairiness or other conspicuous surface features on the upper side of the blade. Select up to two when both reliably apply.",
        icon: false,
        maxSelections: 2,
        options: [
          ["glabrous", "Glabrous"],
          ["pubescent", "Pubescent"],
          ["tomentose", "Tomentose"],
          ["villous", "Villous"],
          ["hirsute", "Hirsute"],
          ["scabrous", "Scabrous"],
          ["papillose", "Papillose"],
          ["waxy", "Waxy or glaucous"],
        ],
      },
      {
        key: "lowerLeafSurface",
        label: "Lower surface traits (abaxial)",
        hint: "Hairiness or other conspicuous surface features on the underside of the blade. Select up to two when both reliably apply.",
        copyFrom: "upperLeafSurface",
        copyLabel: "Same as upper",
        icon: false,
        maxSelections: 2,
        options: [
          ["glabrous", "Glabrous"],
          ["pubescent", "Pubescent"],
          ["tomentose", "Tomentose"],
          ["villous", "Villous"],
          ["hirsute", "Hirsute"],
          ["scabrous", "Scabrous"],
          ["papillose", "Papillose"],
          ["waxy", "Waxy or glaucous"],
        ],
      },
      {
        key: "glands",
        label: "Leaf glands",
        hint: "How readily secretory structures can be observed on the leaf.",
        icon: false,
        options: [
          ["absent", "No leaf glands"],
          ["conspicuous", "Conspicuous glands"],
          ["inconspicuous", "Inconspicuous glands"],
          ["pellucid", "Pellucid glands"],
        ],
      },
      {
        key: "glandPosition",
        label: "Leaf gland position",
        hint: "Select every position where glands reliably occur.",
        icon: false,
        maxSelections: 4,
        showWhen: (traits) => traits.glands && traits.glands !== "absent",
        options: [
          ["blade", "On the blade"],
          ["margin", "Along the margin"],
          ["base", "At the blade base"],
          ["veins", "Along the veins"],
          ["petiole", "On the petiole"],
          ["stipules", "On the stipules"],
        ],
      },
      {
        key: "domatia",
        label: "Domatia",
        hint: "Small structures, often in vein axils on the lower surface, that shelter mites.",
        icon: false,
        options: [
          ["absent", "No domatia"],
          ["tuft", "Hair-tuft domatia"],
          ["pit", "Pit domatia"],
          ["pocket", "Pocket domatia"],
          ["crypt", "Crypt domatia"],
          ["inconspicuous", "Inconspicuous domatia"],
        ],
      },
    ],
  },
  {
    id: "gymnospermReproduction",
    label: "Reproductive structures",
    shortLabel: "Reproduction",
    reproductiveGroups: ["gymnosperm"],
    fields: [
      {
        key: "gymnospermSexualSystem",
        label: "Distribution of reproductive structures",
        hint: "Whether pollen- and ovule-bearing structures occur on the same or separate individuals.",
        icon: false,
        options: [
          ["monoecious", "Monoecious"],
          ["dioecious", "Dioecious"],
        ],
      },
      {
        key: "pollenStructure",
        label: "Pollen-bearing structure",
        hint: "The characteristic structure that bears pollen-producing organs.",
        icon: false,
        options: [
          ["pollen-cone", "Pollen cone"],
          ["catkin-like-strobilus", "Catkin-like pollen strobilus"],
          ["reduced-pollen-structure", "Reduced pollen-bearing structure"],
        ],
      },
      {
        key: "ovuleStructure",
        label: "Ovule-bearing structure",
        hint: "The characteristic structure on which exposed ovules or seeds are borne.",
        icon: false,
        options: [
          ["seed-cone", "Ovulate or seed cone"],
          ["reduced-cone", "Reduced seed cone"],
          ["megasporophylls", "Megasporophylls"],
          ["paired-ovules-on-stalk", "Paired ovules on a stalk"],
          ["solitary-ovule", "Solitary ovule"],
        ],
      },
      {
        key: "seedCovering",
        label: "Seed outer covering",
        hint: "The conspicuous outer seed coat or accessory tissue around the mature seed.",
        icon: false,
        options: [
          ["dry", "Dry"],
          ["fleshy-outer-coat", "Fleshy outer seed coat"],
          ["aril", "Aril"],
          ["fleshy-accessory-tissue", "Fleshy accessory tissue"],
        ],
      },
      {
        key: "seedColor",
        label: "Mature seed color",
        hint: "Select up to three colors shown by the mature seed or its conspicuous outer covering.",
        maxSelections: 3,
        options: [
          ["green", "Green"],
          ["yellow", "Yellow"],
          ["orange", "Orange"],
          ["red", "Red"],
          ["maroon", "Maroon"],
          ["pink", "Pink"],
          ["purple", "Purple"],
          ["blue", "Blue"],
          ["black", "Black"],
          ["brown", "Brown"],
          ["cream", "Cream"],
          ["gray", "Gray"],
          ["white", "White"],
        ],
      },
    ],
  },
  {
    id: "flowers",
    label: "Flowers",
    shortLabel: "Flowers",
    reproductiveGroups: ["angiosperm"],
    fields: [
      {
        key: "flowerPresentation",
        label: "Flower presentation",
        hint: "Whether flowers occur alone or in a group.",
        options: [
          ["solitary", "Solitary"],
          ["inflorescence", "In an inflorescence"],
        ],
      },
      {
        key: "inflorescenceType",
        label: "Inflorescence type",
        hint: "The closest arrangement of grouped flowers.",
        showWhen: (traits) => traits.flowerPresentation === "inflorescence",
        options: [
          ["spike", "Spike"],
          ["raceme", "Raceme"],
          ["panicle", "Panicle"],
          ["umbel", "Umbel"],
          ["corymb", "Corymb"],
          ["head", "Head"],
          ["cyme", "Cyme"],
          ["catkin", "Catkin"],
          ["spadix", "Spadix"],
        ],
      },
      {
        key: "flowerSymmetry",
        label: "Flower symmetry",
        hint: "The symmetry of a typical flower.",
        options: [
          ["radial", "Radial"],
          ["bilateral", "Bilateral"],
          ["bilateral-resupinate", "Bilateral and resupinate"],
          ["asymmetric", "Asymmetric"],
        ],
      },
      {
        key: "flowerSexuality",
        label: "Flower sexuality",
        hint: "The sexual organs present in individual flowers.",
        icon: false,
        options: [
          ["perfect", "Bisexual (perfect)"],
          ["unisexual", "Unisexual"],
          ["mixed", "Bisexual and unisexual flowers"],
        ],
      },
      {
        key: "unisexualSystem",
        label: "Distribution of unisexual flowers",
        hint: "Whether male and female flowers occur on the same or separate individuals.",
        icon: false,
        showWhen: (traits) => traits.flowerSexuality === "unisexual",
        options: [
          ["monoecious", "Monoecious"],
          ["dioecious", "Dioecious"],
        ],
      },
      {
        key: "mixedSexualSystem",
        label: "Mixed sexual system",
        hint: "How bisexual, male, and female flowers or individuals are distributed.",
        icon: false,
        showWhen: (traits) => traits.flowerSexuality === "mixed",
        options: [
          ["andromonoecious", "Andromonoecious"],
          ["gynomonoecious", "Gynomonoecious"],
          ["trimonoecious", "Trimonoecious"],
          ["androdioecious", "Androdioecious"],
          ["gynodioecious", "Gynodioecious"],
          ["trioecious", "Trioecious"],
          ["polygamodioecious", "Polygamodioecious"],
          ["polygamous", "Polygamous or complex"],
        ],
      },
      {
        key: "perianthType",
        label: "Sepals and petals",
        hint: "First choose how the flower is organized. The applicable sepal, petal, or tepal count fields will then appear below.",
        icon: false,
        options: [
          ["absent", "No perianth"],
          ["sepals-only", "Sepals only"],
          ["petals-only", "Petals only"],
          ["sepals-petals", "Both sepals and petals"],
          ["tepals", "Tepals instead"],
        ],
      },
      {
        key: "sepalNumber",
        label: "Number of sepals",
        hint: "Select one exact count, or two counts to record the usual range.",
        icon: false,
        maxSelections: 2,
        showWhen: (traits) =>
          ["sepals-only", "sepals-petals"].includes(traits.perianthType),
        options: FLORAL_PART_COUNT_OPTIONS,
      },
      {
        key: "petalNumber",
        label: "Number of petals",
        hint: "Select one exact count, or two counts to record the usual range.",
        icon: false,
        maxSelections: 2,
        showWhen: (traits) =>
          ["petals-only", "sepals-petals"].includes(traits.perianthType),
        options: FLORAL_PART_COUNT_OPTIONS,
      },
      {
        key: "tepalNumber",
        label: "Number of tepals",
        hint: "Select one exact count, or two counts to record the usual range.",
        icon: false,
        maxSelections: 2,
        showWhen: (traits) => traits.perianthType === "tepals",
        options: FLORAL_PART_COUNT_OPTIONS,
      },
      {
        key: "flowerColor",
        label: "Typical flower color",
        hint: "Select up to three flower colors.",
        maxSelections: 3,
        options: [
          ["white", "White"],
          ["cream", "Cream"],
          ["yellow", "Yellow"],
          ["orange", "Orange"],
          ["red", "Red"],
          ["maroon", "Maroon"],
          ["pink", "Pink"],
          ["peach", "Peach"],
          ["purple", "Purple"],
          ["lavender", "Lavender"],
          ["blue", "Blue"],
          ["green", "Green"],
          ["brown", "Brown"],
          ["gray", "Gray"],
          ["black", "Black"],
        ],
      },
      {
        key: "ovaryPosition",
        label: "Ovary position",
        hint: "The ovary's position relative to the other floral parts.",
        icon: false,
        options: [
          ["superior", "Superior"],
          ["inferior", "Inferior"],
          ["half-inferior", "Half-inferior"],
        ],
      },
    ],
  },
  {
    id: "fruits",
    label: "Fruits",
    shortLabel: "Fruits",
    reproductiveGroups: ["angiosperm"],
    fields: [
      {
        key: "fruitClass",
        label: "Fruit class",
        hint: "The botanical fruit type.",
        options: [
          ["berry", "Berry"],
          ["drupe", "Drupe"],
          ["capsule", "Capsule"],
          ["legume", "Legume"],
          ["follicle", "Follicle"],
          ["achene", "Achene"],
          ["caryopsis", "Caryopsis"],
          ["nut", "Nut"],
          ["samara", "Samara"],
          ["schizocarp", "Schizocarp"],
          ["silique", "Silique or silicle"],
          ["pome", "Pome"],
          ["pepo", "Pepo"],
          ["hesperidium", "Hesperidium"],
          ["aggregate", "Aggregate fruit"],
          ["multiple", "Multiple fruit"],
        ],
      },
      {
        key: "fruitTexture",
        label: "Fruit texture",
        hint: "Whether the mature fruit is fleshy or dry.",
        options: [
          ["fleshy", "Fleshy"],
          ["dry", "Dry"],
        ],
      },
      {
        key: "fruitDehiscence",
        label: "Fruit opening",
        hint: "Whether the mature fruit opens to release seeds.",
        options: [
          ["dehiscent", "Dehiscent"],
          ["indehiscent", "Indehiscent"],
        ],
      },
      {
        key: "fruitColor",
        label: "Mature fruit color",
        hint: "Select up to three colors commonly shown by mature fruits.",
        maxSelections: 3,
        options: [
          ["green", "Green"],
          ["yellow", "Yellow"],
          ["orange", "Orange"],
          ["red", "Red"],
          ["maroon", "Maroon"],
          ["pink", "Pink"],
          ["purple", "Purple"],
          ["blue", "Blue"],
          ["black", "Black"],
          ["brown", "Brown"],
          ["cream", "Cream"],
          ["gray", "Gray"],
          ["white", "White"],
        ],
      },
    ],
  },
  {
    id: "diagnostic",
    label: "Other traits",
    shortLabel: "Other",
    fields: [
      {
        key: "odor",
        label: "Odor",
        hint: "A characteristic odor from leaves, bark, or other tissue.",
        options: [
          ["none", "No notable odor"],
          ["aromatic", "Aromatic"],
          ["strong", "Strong"],
          ["unpleasant", "Unpleasant"],
        ],
      },
    ],
  },
];

const LEGACY_FIELD_LOOKUP = {
  leafSurface: {
    key: "leafSurface",
    label: "Leaf surface",
    hint: "Legacy overall leaf-surface description from before upper and lower surfaces were recorded separately.",
    icon: false,
    options: [
      ["glabrous", "Glabrous"],
      ["pubescent", "Pubescent"],
      ["tomentose", "Tomentose"],
      ["villous", "Villous"],
      ["hirsute", "Hirsute"],
      ["scabrous", "Scabrous"],
      ["papillose", "Papillose"],
      ["waxy", "Waxy or glaucous"],
    ],
  },
};

const FIELD_LOOKUP = {
  ...DESCRIPTION_SECTIONS.reduce((lookup, section) => {
    section.fields.forEach((field) => {
      lookup[field.key] = field;
    });
    return lookup;
  }, {}),
  ...LEGACY_FIELD_LOOKUP,
};

const DESCRIPTION_DISPLAY_GROUPS = [
  {
    id: "habit",
    label: "Habit",
    fields: ["growthForm", "stemHabit", "armature"],
  },
  {
    id: "leaves",
    label: "Leaves",
    fields: [
      "leafPersistence",
      "leafArrangement",
      "leafComplexity",
      "compoundType",
      "leafShape",
      "leafMargin",
      "leafApex",
      "leafBase",
      "leafTexture",
      "venation",
      "stipules",
    ],
  },
  {
    id: "leafSurfaces",
    label: "Leaf surfaces",
    fields: [
      "upperLeafColor",
      "lowerLeafColor",
      "upperLeafSurface",
      "lowerLeafSurface",
      "leafSurface",
      "glands",
      "glandPosition",
      "domatia",
    ],
  },
  {
    id: "petiole",
    label: "Petiole",
    fields: [
      "petioleLength",
      "petioleForm",
      "petiolePulvinusPosition",
      "petioleGlands",
    ],
  },
  {
    id: "reproduction",
    label: "Reproduction",
    fields: [
      "gymnospermSexualSystem",
      "pollenStructure",
      "ovuleStructure",
      "seedCovering",
      "seedColor",
    ],
  },
  {
    id: "flowers",
    label: "Flowers",
    fields: [
      "flowerPresentation",
      "inflorescenceType",
      "flowerSymmetry",
      "flowerSexuality",
      "unisexualSystem",
      "mixedSexualSystem",
      "perianthType",
      "sepalNumber",
      "petalNumber",
      "tepalNumber",
      "flowerColor",
      "ovaryPosition",
    ],
  },
  {
    id: "fruits",
    label: "Fruits",
    fields: ["fruitClass", "fruitTexture", "fruitDehiscence", "fruitColor"],
  },
  {
    id: "other",
    label: "Other traits",
    fields: ["exudate", "odor"],
  },
];

const OPTION_HELP = {
  growthForm: {
    tree: "A woody perennial with a dominant trunk and an elevated crown.",
    shrub: "A woody perennial with several stems arising near the base.",
    herb: "A plant whose above-ground stems are not persistently woody.",
    vine: "A weak-stemmed plant that trails or climbs using external support.",
    liana: "A long-lived woody climber rooted in the ground.",
    epiphyte:
      "A plant growing on another plant for support without parasitizing it.",
    palm: "A palm-like plant, typically with a crown of large leaves on a trunk.",
    grass: "A grass-like plant with narrow leaves and usually jointed stems.",
    sedge:
      "A grass-like plant, often with solid triangular stems in the sedge family.",
    fern: "A vascular plant with fronds that reproduces by spores rather than flowers.",
    aquatic: "A plant adapted to grow submerged, floating, or rooted in water.",
  },
  stemHabit: {
    erect: "Stems grow predominantly upright.",
    climbing: "Stems ascend by relying on another structure for support.",
    creeping: "Stems grow horizontally along the ground and may root at nodes.",
    trailing:
      "Stems spread across the ground or hang without regularly rooting at nodes.",
    rosette:
      "Leaves or short stems form a compact circular cluster near the base.",
    tufted: "Many shoots arise close together to form a dense clump.",
  },
  armature: {
    absent: "No conspicuous spines, thorns, or prickles are present.",
    spines: "Sharp structures derived from leaves, leaf parts, or stipules.",
    thorns: "Sharp structures derived from modified stems or branches.",
    prickles:
      "Sharp outgrowths of the surface tissue that detach relatively easily.",
  },
  leafPersistence: {
    evergreen:
      "Leaves are retained through the year rather than shed seasonally.",
    deciduous:
      "Most leaves are shed during a regular season or environmental period.",
    seasonal:
      "The plant becomes conspicuously leafless during part of the year.",
  },
  leafArrangement: {
    alternate:
      "One leaf emerges at each node, alternating from side to side along the stem.",
    opposite: "Two leaves emerge on opposite sides of the same stem node.",
    whorled: "Three or more leaves emerge around the same stem node.",
    basal: "Leaves arise mainly from the base of the plant.",
    rosette: "Basal leaves form a compact, approximately circular cluster.",
    fascicled: "Several leaves arise together in a tight bundle or fascicle.",
  },
  leafComplexity: {
    simple: "Each leaf has one continuous blade, even if that blade is lobed.",
    compound: "Each leaf is divided into two or more distinct leaflets.",
  },
  compoundType: {
    unifoliolate:
      "A compound leaf reduced to a single leaflet, usually recognizable by a joint between the leaflet and petiole.",
    bifoliate: "The compound leaf consists of two leaflets.",
    trifoliate: "The compound leaf consists of three leaflets.",
    imparipinnate:
      "Leaflets are arranged along a central rachis that ends in a single terminal leaflet.",
    paripinnate:
      "Leaflets are arranged along a central rachis that ends in a terminal pair rather than a single terminal leaflet.",
    bipinnate:
      "The primary divisions of the leaf are themselves pinnately divided.",
    tripinnate:
      "The leaf is divided pinnately through three successive branching levels.",
    palmate: "Leaflets radiate from one point at the end of the petiole.",
  },

  leafShape: {
    elliptic: "Widest near the middle and tapering similarly toward both ends.",
    ovate: "Egg-shaped and widest below the middle, toward the base.",
    obovate: "Reverse egg-shaped and widest above the middle, toward the tip.",
    oblong:
      "Longer than wide, with sides nearly parallel for much of the blade and rounded ends.",
    lanceolate:
      "Long and narrow, widest below the middle, and tapering to the tip.",
    oblanceolate:
      "Long and narrow, widest above the middle, and tapering toward the base.",
    linear:
      "Very narrow with sides that remain nearly parallel for most of the blade.",
    acicular:
      "Needle-shaped, very narrow, and tapering gradually toward a sharp tip.",
    spatulate:
      "Spoon-shaped, narrow toward the base and broadly rounded toward the tip.",
    cordate: "Heart-shaped, usually with a notch at the base.",
    reniform:
      "Kidney-shaped, broader than long and usually notched where the petiole attaches.",
    orbicular: "Approximately circular in outline.",
    deltoid:
      "Broadly triangular, usually with a wide base and narrower pointed tip.",
    rhombic:
      "Diamond-shaped, widest near the middle and tapering toward both tip and base.",
    sagittate: "Arrowhead-shaped with basal lobes directed backward.",
    hastate:
      "Spearhead-shaped with basal lobes spreading outward rather than pointing backward.",
    flabellate:
      "Fan-shaped, broadening outward from a narrow base, as in Ginkgo.",
  },
  leafMargin: {
    entire: "The leaf edge is smooth and lacks teeth or lobes.",
    serrate: "The edge has saw-like teeth pointing toward the leaf tip.",
    dentate:
      "The edge has outward-pointing teeth rather than forward-pointing teeth.",
    crenate: "The edge has rounded, scallop-like teeth.",
    lobed: "Deep or shallow projections divide the edge into distinct lobes.",
    undulate: "The edge is smoothly wavy rather than toothed.",
    spinose: "The edge bears stiff, sharp, spine-like points.",
  },
  leafApex: {
    acute: "The tip narrows to an angle of less than approximately 90 degrees.",
    acuminate: "The tip tapers gradually into a prolonged narrow point.",
    obtuse: "The tip ends in a broad, blunt angle.",
    rounded: "The tip forms a smooth rounded curve.",
    emarginate: "The tip has a shallow notch.",
    mucronate: "The tip ends abruptly in a short, small point.",
  },
  leafBase: {
    cuneate: "The blade narrows toward the base like a wedge.",
    obtuse: "The blade meets the petiole through a broad, blunt basal angle.",
    rounded: "The base forms a smooth rounded curve.",
    cordate: "The base is heart-shaped with a distinct notch.",
    truncate: "The base appears cut straight across.",
    oblique: "The two sides of the blade meet the base at unequal levels.",
    attenuate: "The blade tapers gradually into a long, narrow base.",
  },
  leafTexture: {
    membranous: "Thin, soft, and flexible like a delicate membrane.",
    chartaceous: "Thin but firm, with a paper-like texture.",
    coriaceous: "Thick, firm, and leathery.",
    succulent: "Thickened and fleshy because the tissue stores water.",
  },
  leafSurface: {
    glabrous: "The surface lacks hairs.",
    pubescent: "The surface is visibly covered with hairs.",
    tomentose:
      "The surface is densely covered by short, matted or woolly hairs.",
    villous: "The surface bears long, soft, shaggy hairs.",
    hirsute: "The surface bears relatively coarse, stiff hairs.",
    scabrous:
      "The surface feels rough because of short stiff hairs or projections.",
    papillose: "The surface bears minute nipple-like projections or papillae.",
    waxy: "The surface has a pale or wax-like coating, often called glaucous.",
  },
  upperLeafSurface: {
    glabrous: "The upper (adaxial) surface lacks hairs.",
    pubescent: "The upper (adaxial) surface is visibly covered with hairs.",
    tomentose:
      "The upper (adaxial) surface is densely covered by short, matted or woolly hairs.",
    villous: "The upper (adaxial) surface bears long, soft, shaggy hairs.",
    hirsute: "The upper (adaxial) surface bears relatively coarse, stiff hairs.",
    scabrous:
      "The upper (adaxial) surface feels rough because of short stiff hairs or projections.",
    papillose:
      "The upper (adaxial) surface bears minute nipple-like projections or papillae.",
    waxy:
      "The upper (adaxial) surface has a pale or wax-like coating, often called glaucous.",
  },
  lowerLeafSurface: {
    glabrous: "The lower (abaxial) surface lacks hairs.",
    pubescent: "The lower (abaxial) surface is visibly covered with hairs.",
    tomentose:
      "The lower (abaxial) surface is densely covered by short, matted or woolly hairs.",
    villous: "The lower (abaxial) surface bears long, soft, shaggy hairs.",
    hirsute: "The lower (abaxial) surface bears relatively coarse, stiff hairs.",
    scabrous:
      "The lower (abaxial) surface feels rough because of short stiff hairs or projections.",
    papillose:
      "The lower (abaxial) surface bears minute nipple-like projections or papillae.",
    waxy:
      "The lower (abaxial) surface has a pale or wax-like coating, often called glaucous.",
  },
  venation: {
    pinnate: "Secondary veins branch from one main midvein.",
    palmate: "Several major veins radiate from one point near the leaf base.",
    parallel: "The major veins run approximately parallel along the blade.",
    arcuate: "Secondary veins curve toward the tip in a series of arches.",
    dichotomous: "Veins repeatedly divide into two similar branches.",
    triplinerved:
      "Three prominent primary veins arise together at or just above the leaf base.",
  },
  stipules: {
    absent: "No stipules are visible at the base of the petiole.",
    "free-lateral":
      "A separate stipule occurs on each side of the petiole base.",
    interpetiolar:
      "Stipules of opposite leaves meet or fuse between the two petioles, as commonly seen in Rubiaceae.",
    intrapetiolar:
      "The pair of stipules belonging to one leaf fuse on the inner side of its petiole.",
    adnate: "Stipules are fused for part of their length to the petiole.",
    ochreate:
      "Fused stipules form a sheath or ochrea around the stem above the node.",
    foliaceous: "Stipules are enlarged and leaf-like.",
    spinescent: "Stipules are modified into stiff defensive spines.",
    caducous: "Stipules are shed early and may leave scars at the node.",
    inconspicuous:
      "Stipules are present but very small, short-lived, or difficult to see.",
  },
  glands: {
    absent:
      "No leaf glands are evident after ordinary inspection or backlighting.",
    conspicuous:
      "Distinct secretory glands are readily visible without magnification.",
    inconspicuous:
      "Glands are present but require close inspection or magnification.",
    pellucid:
      "Translucent glandular dots or streaks become visible when the leaf is held against a bright light.",
  },
  glandPosition: {
    blade: "Glands occur on the lamina away from its edge and major veins.",
    margin: "Glands occur along the leaf edge or at tooth tips.",
    base: "Glands are concentrated at the base of the leaf blade.",
    veins: "Glands occur on or beside the major veins.",
    petiole: "Glands occur on the leaf stalk.",
    stipules: "Glands or colleters occur on the stipules.",
  },
  domatia: {
    absent:
      "No domatia are visible, especially in the vein axils beneath the leaf.",
    tuft: "A localized tuft of hairs forms a shelter, usually in a vein axil beneath the leaf.",
    pit: "A small pit forms a domatium, commonly near a vein junction.",
    pocket:
      "A flap or pocket of tissue creates a small shelter beneath the leaf.",
    crypt: "A deeper enclosed chamber or crypt forms the domatium.",
    inconspicuous:
      "Domatia are present but require close inspection to recognize.",
  },
  petioleLength: {
    sessile: "The leaf blade attaches directly to the stem without a petiole.",
    subsessile:
      "A petiole is present but is shorter than approximately 0.5 cm.",
    short: "A typical mature petiole is approximately 0.5–2 cm long.",
    medium: "A typical mature petiole is approximately 2–5 cm long.",
    long: "A typical mature petiole is longer than approximately 5 cm.",
  },
  petioleForm: {
    terete: "The petiole is approximately cylindrical in cross-section.",
    canaliculate:
      "A longitudinal channel or groove runs along the upper side of the petiole.",
    flattened:
      "The petiole is conspicuously compressed rather than cylindrical.",
    winged: "Flattened blade-like wings extend along the petiole.",
    pulvinate:
      "A thickened flexible pulvinus occurs at one or both ends of the petiole.",
  },
  petiolePulvinusPosition: {
    basal: "The pulvinus occurs at the base of the petiole near the stem.",
    apical: "The pulvinus occurs at the apex of the petiole near the blade.",
    both: "Pulvini occur at both the basal and apical ends of the petiole.",
  },
  petioleGlands: {
    absent: "No glands are visible on the petiole.",
    apical:
      "One or more glands occur near the junction between petiole and blade.",
    basal:
      "One or more glands occur near the junction between petiole and stem.",
    medial: "Glands occur along the central portion of the petiole.",
    multiple: "Glands occur in more than one region of the petiole.",
  },
  exudate: {
    absent:
      "Damaged tissue does not release a conspicuous sap, latex, or resin.",
    clear:
      "Damaged tissue releases a transparent or nearly transparent liquid.",
    "white-latex": "Damaged tissue releases an opaque white latex.",
    "yellow-latex": "Damaged tissue releases yellow latex.",
    "orange-latex": "Damaged tissue releases orange latex.",
    "red-latex": "Damaged tissue releases red latex.",
    "other-colored-latex":
      "Damaged tissue releases colored latex that is not white, yellow, orange, or red.",
  },
  flowerPresentation: {
    solitary:
      "A flower is borne alone rather than as part of a flower cluster.",
    inflorescence:
      "Multiple flowers are organized together on a shared flowering structure.",
  },
  inflorescenceType: {
    spike: "Sessile flowers attach directly along an unbranched central axis.",
    raceme: "Stalked flowers attach along an unbranched central axis.",
    panicle:
      "The flowering axis is branched, with flowers borne on those branches.",
    umbel: "Several flower stalks arise from approximately the same point.",
    corymb:
      "Lower flower stalks are longer, producing a roughly flat-topped cluster.",
    head: "Many small sessile flowers are crowded onto a compact receptacle.",
    cyme: "The terminal or central flower opens first, limiting further growth of that axis.",
    catkin:
      "A usually hanging, slender cluster bears many small unisexual flowers.",
    spadix:
      "Small flowers are crowded on a thick fleshy axis, often accompanied by a spathe.",
  },
  flowerSymmetry: {
    radial:
      "The flower can be divided into similar halves along several planes.",
    bilateral:
      "The flower can be divided into matching halves along only one plane.",
    "bilateral-resupinate":
      "The flower is bilaterally symmetric and rotated during development so the lip is repositioned, as in many orchids.",
    asymmetric:
      "The flower lacks a plane that divides it into matching halves.",
  },
  flowerSexuality: {
    perfect:
      "Individual flowers contain both functional stamens and carpels; these are also called bisexual or perfect flowers.",
    unisexual:
      "Individual flowers are either staminate (male) or pistillate (female), rather than containing both functional sexes.",
    mixed: "The species produces both bisexual flowers and unisexual flowers.",
  },
  unisexualSystem: {
    monoecious:
      "Male and female unisexual flowers occur on the same individual plant.",
    dioecious:
      "Male and female unisexual flowers occur on separate individual plants.",
  },
  mixedSexualSystem: {
    andromonoecious: "Bisexual and male flowers occur on the same individual.",
    gynomonoecious: "Bisexual and female flowers occur on the same individual.",
    trimonoecious:
      "Bisexual, male, and female flowers all occur on the same individual.",
    androdioecious:
      "The population contains male individuals and bisexual individuals.",
    gynodioecious:
      "The population contains female individuals and bisexual individuals.",
    trioecious:
      "Male, female, and bisexual individuals occur within the population.",
    polygamodioecious:
      "Individuals are predominantly male or female, but bisexual flowers or mixed sexual expression also occur within the population.",
    polygamous:
      "The species has a more complex mixture of bisexual and unisexual flowers or individuals.",
  },
  gymnospermSexualSystem: {
    monoecious:
      "Pollen-bearing and ovule-bearing reproductive structures occur on the same individual plant.",
    dioecious:
      "Pollen-bearing and ovule-bearing reproductive structures occur on separate individual plants.",
  },
  pollenStructure: {
    "pollen-cone":
      "Pollen is produced in a compact cone or microstrobilus bearing many microsporophylls or microsporangiophores.",
    "catkin-like-strobilus":
      "Pollen is produced on a slender, catkin-like strobilus with many pollen-bearing units along an axis, as in Ginkgo.",
    "reduced-pollen-structure":
      "The pollen-bearing structure is strongly reduced and does not form a typical compact cone.",
  },
  ovuleStructure: {
    "seed-cone":
      "Exposed ovules are borne on the scales or sporophylls of an ovulate cone that later carries the seeds.",
    "reduced-cone":
      "The ovule-bearing cone is strongly reduced, often to one or a few fertile units.",
    megasporophylls:
      "Ovules are borne on megasporophylls rather than within a compact seed cone.",
    "paired-ovules-on-stalk":
      "A stalk terminates in a pair of exposed ovules, commonly with only one developing to maturity, as in Ginkgo.",
    "solitary-ovule":
      "A single exposed ovule is borne in a highly reduced female reproductive structure.",
  },
  seedCovering: {
    dry: "The mature seed has no conspicuous fleshy outer covering.",
    "fleshy-outer-coat":
      "The mature seed has a fleshy outer seed-coat layer, such as the sarcotesta of Ginkgo.",
    aril: "A distinct fleshy aril partly or completely surrounds the seed.",
    "fleshy-accessory-tissue":
      "Fleshy accessory tissue associated with the reproductive structure surrounds or supports the mature seed.",
  },
  perianthType: {
    absent:
      "The flower lacks a perianth; neither petals, sepals, nor tepals are present.",
    "sepals-only":
      "The perianth consists of sepals without a distinct corolla of petals.",
    "petals-only":
      "The perianth consists of petal-like members without a distinct calyx of sepals.",
    "sepals-petals":
      "The perianth is differentiated into an outer calyx of sepals and an inner corolla of petals.",
    tepals:
      "The perianth segments are not differentiated into distinct sepals and petals and are therefore described as tepals.",
  },
  petalNumber: {
    absent: "The flower lacks conspicuous petals or tepals.",
    three:
      "A typical flower has three petals, or three tepals when the perianth is undifferentiated.",
    four: "A typical flower has four petals, or four tepals when the perianth is undifferentiated.",
    five: "A typical flower has five petals, or five tepals when the perianth is undifferentiated.",
    six: "A typical flower has six petals, or six tepals when the perianth is undifferentiated.",
    many: "A typical flower has more than six petals or tepals.",
  },
  ovaryPosition: {
    superior:
      "The ovary sits above the attachment of the sepals, petals, and stamens.",
    inferior: "The ovary sits below the attachment of the other floral parts.",
    "half-inferior":
      "The ovary is partly embedded, with floral parts attached near its midpoint.",
  },
  fruitClass: {
    berry:
      "A fleshy fruit whose inner fruit wall remains soft around one or more seeds.",
    drupe:
      "A fleshy fruit with a hardened inner layer forming a stone around the seed.",
    capsule:
      "A dry fruit formed from multiple carpels that opens to release seeds.",
    legume: "A dry fruit from one carpel that typically opens along two seams.",
    follicle: "A dry fruit from one carpel that opens along a single seam.",
    achene:
      "A small dry one-seeded fruit that does not open and is not fused to the seed.",
    caryopsis:
      "A dry one-seeded fruit in which the fruit wall is fused to the seed coat, typical of grasses.",
    nut: "A hard, dry, usually one-seeded fruit that does not open at maturity.",
    samara: "A dry fruit with a wing that aids dispersal by wind.",
    schizocarp:
      "A dry fruit that splits into separate one-seeded segments at maturity.",
    silique:
      "A dry elongated fruit that opens along two sides and leaves a central partition.",
    pome: "A fleshy accessory fruit with a papery or cartilaginous core.",
    pepo: "A berry with a firm rind, characteristic of squashes and their relatives.",
    hesperidium:
      "A berry with a leathery rind and segmented juicy interior, characteristic of citrus.",
    cone: "A cone-like reproductive structure bearing seeds on scales.",
    aggregate:
      "One flower produces a cluster of small fruit units from multiple ovaries.",
    multiple:
      "The ovaries of several flowers develop together into one fruiting body.",
  },
  fruitTexture: {
    fleshy: "The mature fruit wall is soft or juicy.",
    dry: "The mature fruit wall is dry rather than fleshy.",
  },
  fruitDehiscence: {
    dehiscent: "The mature fruit opens naturally to release its seeds.",
    indehiscent:
      "The mature fruit remains closed and does not release seeds by opening.",
  },
  odor: {
    none: "Leaves, bark, or damaged tissue have no conspicuous diagnostic odor.",
    aromatic: "Tissue releases a distinctive pleasant or spice-like odor.",
    strong: "Tissue releases an intense, readily noticeable odor.",
    unpleasant: "Tissue releases a distinctive unpleasant odor.",
  },
  climbingStructures: {
    absent: "No specialized climbing or attachment structure is visible.",
    tendrils: "Slender coiling organs wrap around supports.",
    hooks: "Hooked structures catch onto surrounding vegetation or surfaces.",
    "twining-stems": "The main stems coil around a support as they grow.",
    "clinging-roots":
      "Adventitious roots attach the stem to a supporting surface.",
  },
  specialRoots: {
    none: "No conspicuous specialized root form is present.",
    buttress:
      "Large flattened roots flare from the lower trunk and support the plant.",
    prop: "Roots descend from stems or branches and provide additional support.",
    aerial: "Roots grow above the soil and remain exposed to the air.",
    pneumatophore:
      "Upward-growing roots project above waterlogged soil for gas exchange.",
    tuberous: "Roots are enlarged and fleshy for storage.",
  },
};

function getOptionHelp(field, value, label) {
  const detailedHelp = OPTION_HELP[field.key]?.[value];

  if (detailedHelp) return detailedHelp;

  if (["sepalNumber", "petalNumber", "tepalNumber"].includes(field.key)) {
    const organ =
      field.key === "sepalNumber"
        ? "sepals"
        : field.key === "petalNumber"
          ? "petals"
          : "tepals";

    if (value === "many") {
      return `A typical flower has numerous ${organ}.`;
    }

    if (value === "variable") {
      return `The number of ${organ} varies within the species.`;
    }

    return `A typical flower has ${label} ${organ}.`;
  }

  if (["upperLeafColor", "lowerLeafColor"].includes(field.key)) {
    const side = field.key === "upperLeafColor" ? "upper (adaxial)" : "lower (abaxial)";
    return `The ${side} surface of a mature, healthy leaf is typically ${label.toLowerCase()}.`;
  }

  if (field.key === "flowerColor") {
    return value === "variable"
      ? "Flower color varies conspicuously within the species."
      : `The petals or other showy floral parts are typically ${label.toLowerCase()}.`;
  }

  if (field.key === "fruitColor") {
    return value === "variable"
      ? "Mature fruit color varies conspicuously within the species."
      : `The fruit is typically ${label.toLowerCase()} when mature.`;
  }

  if (field.key === "seedColor") {
    return `The mature seed or its conspicuous outer covering is typically ${label.toLowerCase()}.`;
  }

  return `${label}. ${field.hint}`;
}

function getTraitIconType(fieldKey, value) {
  if (fieldKey === "growthForm") return value;
  if (fieldKey === "stemHabit") {
    if (value === "climbing") return "vine";
    if (value === "creeping") return "creeping";
    if (value === "trailing") return "trailing";
    if (value === "rosette") return "rosetteHabit";
    if (value === "tufted") return "tufted";
    return "stem";
  }
  if (fieldKey === "armature") {
    if (value === "absent") return "stem";
    return value;
  }
  if (fieldKey === "leafPersistence") return `persistence-${value}`;
  if (fieldKey === "leafArrangement") return value;
  if (fieldKey === "leafComplexity") {
    return value === "simple" ? "leaf" : "compound";
  }

  if (fieldKey === "compoundType") {
    if (value === "unifoliolate") return "compoundUnifoliolate";
    if (value === "bifoliate") return "compoundBifoliate";
    if (value === "trifoliate") return "compoundTrifoliate";
    if (value === "imparipinnate") return "compoundImparipinnate";
    if (value === "paripinnate") return "compoundParipinnate";
    if (value === "bipinnate") return "compoundBipinnate";
    if (value === "tripinnate") return "compoundTripinnate";
    if (value === "palmate") return "compoundPalmate";
  }
  if (fieldKey === "stipules") return `stipule-${value}`;
  if (fieldKey === "exudate") return "drop";

  if (fieldKey === "leafShape") {
    if (value === "elliptic") return "shapeElliptic";
    if (value === "ovate") return "shapeOvate";
    if (value === "obovate") return "shapeObovate";
    if (value === "oblong") return "shapeOblong";
    if (value === "lanceolate") return "shapeLanceolate";
    if (value === "oblanceolate") return "shapeOblanceolate";
    if (value === "linear") return "shapeLinear";
    if (value === "acicular") return "shapeAcicular";
    if (value === "spatulate") return "shapeSpatulate";
    if (value === "cordate") return "shapeCordate";
    if (value === "reniform") return "shapeReniform";
    if (value === "orbicular") return "shapeOrbicular";
    if (value === "deltoid") return "shapeDeltoid";
    if (value === "rhombic") return "shapeRhombic";
    if (value === "sagittate") return "shapeSagittate";
    if (value === "hastate") return "shapeHastate";
    if (value === "flabellate") return "shapeFlabellate";
  }

  if (fieldKey === "leafMargin") {
    if (value === "entire") return "marginEntire";
    if (value === "serrate") return "marginSerrate";
    if (value === "dentate") return "marginDentate";
    if (value === "crenate") return "marginCrenate";
    if (value === "lobed") return "marginLobed";
    if (value === "undulate") return "marginUndulate";
    if (value === "spinose") return "marginSpinose";
  }

  if (fieldKey === "leafApex") return `apex-${value}`;
  if (fieldKey === "leafBase") return `base-${value}`;
  if (fieldKey === "venation") return `venation-${value}`;
  if (fieldKey === "petioleLength") return `petiole-length-${value}`;
  if (fieldKey === "petioleForm") return `petiole-form-${value}`;
  if (fieldKey === "flowerPresentation") {
    return value === "solitary" ? "flower-solitary" : "flower-cluster";
  }
  if (fieldKey === "inflorescenceType") return `inflorescence-${value}`;
  if (fieldKey === "flowerSymmetry") return `symmetry-${value}`;
  if (fieldKey === "flowerSexuality") return `sexuality-${value}`;
  if (fieldKey === "petalNumber") return `petals-${value}`;
  if (fieldKey === "ovaryPosition") return `ovary-${value}`;
  if (fieldKey === "fruitClass") return `fruit-${value}`;
  if (fieldKey === "fruitTexture") return `fruit-texture-${value}`;
  if (fieldKey === "fruitDehiscence") return `fruit-opening-${value}`;

  if (fieldKey.startsWith("leaf")) return "leaf";
  if (
    fieldKey.startsWith("flower") ||
    fieldKey === "petalNumber" ||
    fieldKey === "ovaryPosition"
  )
    return "flower";
  if (fieldKey.startsWith("fruit")) return "fruit";
  if (fieldKey === "odor") return "scent";
  if (fieldKey === "climbingStructures") return "vine";
  if (fieldKey === "specialRoots") return "root";
  return "leaf";
}

function normalizeScientificName(value) {
  return String(value || "")
    .trim()
    .replace(/\s+/g, " ")
    .toLowerCase();
}

function getDescriptionId(scientificName) {
  return encodeURIComponent(normalizeScientificName(scientificName));
}

function normalizeDescriptionTraits(rawTraits, reproductiveGroup = "") {
  const source = rawTraits && typeof rawTraits === "object" ? rawTraits : {};
  const normalized = Object.fromEntries(
    Object.entries(source).filter(([key]) => FIELD_LOOKUP[key]),
  );

  if (
    normalized.compoundType === "pinnate" &&
    ["imparipinnate", "paripinnate"].includes(source.pinnateType)
  ) {
    normalized.compoundType = source.pinnateType;
  }

  if (normalized.exudate === "colored-latex") {
    normalized.exudate = "other-colored-latex";
  }

  if (normalized.exudate === "resinous") {
    delete normalized.exudate;
  }

  if (
    normalized.leafSurface &&
    (normalized.upperLeafSurface || normalized.lowerLeafSurface)
  ) {
    delete normalized.leafSurface;
  }

  if (normalized.flowerSexuality === "bisexual") {
    normalized.flowerSexuality = "perfect";
  }

  if (normalized.flowerSexuality === "both") {
    normalized.flowerSexuality = "mixed";
  }

  FLORAL_PART_COUNT_KEYS.forEach((key) => {
    if (normalized[key] === "variable") {
      delete normalized[key];
      return;
    }

    if (Array.isArray(normalized[key])) {
      normalized[key] = normalized[key].filter((value) => value !== "variable");

      if (normalized[key].length === 0) delete normalized[key];
    }
  });

  /*
   * Legacy Petal number records represented true petals under the old schema.
   * A non-absent count can therefore be retained as a differentiated perianth.
   * "Petals absent" cannot safely distinguish an absent perianth from a
   * sepals-only or tepal-bearing flower, so it is discarded rather than guessed.
   */
  if (normalized.petalNumber === "absent") {
    delete normalized.petalNumber;
  } else if (normalized.petalNumber && !normalized.perianthType) {
    normalized.perianthType = "sepals-petals";
  }

  // "Cone" used to be represented as a fruit class. Cones are gymnosperm
  // reproductive structures, not fruits, so discard that legacy value.
  if (normalized.fruitClass === "cone") {
    delete normalized.fruitClass;
  }

  if (reproductiveGroup === "gymnosperm") {
    ANGIOSPERM_REPRODUCTIVE_TRAIT_KEYS.forEach((key) => {
      delete normalized[key];
    });
  } else if (reproductiveGroup === "angiosperm") {
    GYMNOSPERM_REPRODUCTIVE_TRAIT_KEYS.forEach((key) => {
      delete normalized[key];
    });
  } else if (reproductiveGroup === "spore-bearing") {
    ANGIOSPERM_REPRODUCTIVE_TRAIT_KEYS.forEach((key) => {
      delete normalized[key];
    });
    GYMNOSPERM_REPRODUCTIVE_TRAIT_KEYS.forEach((key) => {
      delete normalized[key];
    });
  }

  return normalized;
}

function descriptionSectionIsVisible(section, reproductiveGroup) {
  if (!Array.isArray(section?.reproductiveGroups)) return true;

  // Preserve the previous flower/fruit editor when taxonomy resolution is
  // unavailable, but do not expose gymnosperm-only controls by default.
  if (!reproductiveGroup) {
    return section.reproductiveGroups.includes("angiosperm");
  }

  return section.reproductiveGroups.includes(reproductiveGroup);
}

function traitLabel(key, value) {
  const field = FIELD_LOOKUP[key];

  if (Array.isArray(value)) {
    const orderedValues = FLORAL_PART_COUNT_KEYS.has(key)
      ? [...value].sort(
          (first, second) =>
            field?.options.findIndex(([optionValue]) => optionValue === first) -
            field?.options.findIndex(([optionValue]) => optionValue === second),
        )
      : value;

    return orderedValues
      .map((item) => {
        const option = field?.options.find(
          ([optionValue]) => optionValue === item,
        );
        return option?.[1] || item;
      })
      .join(
        key === "leafShape" || FLORAL_PART_COUNT_KEYS.has(key) ? "–" : ", ",
      );
  }

  const option = field?.options.find(([optionValue]) => optionValue === value);
  return option?.[1] || value;
}

const TRAIT_DISPLAY_PREFIXES = {
  compoundType: "Compound type",
  leafApex: "Apex",
  leafBase: "Base",
  leafTexture: "Texture",
  upperLeafColor: "Upper color",
  lowerLeafColor: "Lower color",
  upperLeafSurface: "Upper surface",
  lowerLeafSurface: "Lower surface",
  leafSurface: "Surface",
  stipules: "Stipules",
  glandPosition: "Gland position",

  petioleLength: "Length",
  petioleForm: "Form",
  petiolePulvinusPosition: "Pulvinus",

  gymnospermSexualSystem: "Sexual system",
  pollenStructure: "Pollen",
  ovuleStructure: "Ovules",
  seedCovering: "Seed covering",
  seedColor: "Seed color",

  flowerPresentation: "Presentation",
  inflorescenceType: "Inflorescence",
  flowerSymmetry: "Symmetry",
  flowerSexuality: "Sexuality",
  unisexualSystem: "Sexual system",
  mixedSexualSystem: "Sexual system",
  perianthType: "Perianth",
  sepalNumber: "Sepals",
  petalNumber: "Petals",
  tepalNumber: "Tepals",
  flowerColor: "Color",
  ovaryPosition: "Ovary",

  fruitClass: "Type",
  fruitTexture: "Texture",
  fruitDehiscence: "Opening",
  fruitColor: "Color",

  exudate: "Exudate",
  odor: "Odor",
};

function displayTraitLabel(key, value) {
  const label = traitLabel(key, value);
  const prefix = TRAIT_DISPLAY_PREFIXES[key];

  return prefix ? `${prefix} · ${label}` : label;
}

function formatUpdatedAt(value) {
  if (!value) return "";

  const date =
    typeof value.toDate === "function" ? value.toDate() : new Date(value);
  if (Number.isNaN(date.getTime())) return "";

  return new Intl.DateTimeFormat(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  }).format(date);
}

function LeafIcon({ className = "h-5 w-5" }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      className={className}
    >
      <path d="M20.5 3.5C13 3.5 6.2 6.2 4.3 12.1c-1.1 3.4.6 6.4 3.5 7.3 5.8 1.8 10.6-4.3 12.7-15.9Z" />
      <path d="M4.2 20.5c2.8-5.3 7.2-9.2 13.2-12" />
    </svg>
  );
}

const COLOR_SWATCHES = {
  white: "#ffffff",
  cream: "#fff4cf",
  "dark-green": "#28543a",
  green: "#5f9f62",
  "light-green": "#9fca78",
  "yellow-green": "#a7bf45",
  "blue-green": "#5f9f93",
  "gray-green": "#839487",
  silver: "#c7ccca",
  bronze: "#9a6b47",
  yellow: "#f4c430",
  orange: "#ed8b32",
  red: "#d94a4a",
  maroon: "#7f2639",
  pink: "#ec8eaa",
  peach: "#f2aa7e",
  purple: "#8b5fbf",
  lavender: "#b79ad8",
  blue: "#4f83cc",
  brown: "#8a5a3b",
  gray: "#8b9298",
  black: "#25282b",
};

const EXUDATE_SWATCHES = {
  absent: "transparent",
  clear: "#dff7f6",
  "white-latex": "#ffffff",
  "yellow-latex": "#f4c430",
  "orange-latex": "#ed8b32",
  "red-latex": "#d94a4a",
  "other-colored-latex": "#8b5fbf",
};

function PetalPaths({ count }) {
  if (count === 0) {
    return (
      <>
        <circle cx="12" cy="12" r="2.1" />
        <path d="M7 7l10 10M17 7 7 17" />
      </>
    );
  }

  const petalCount = count === "many" ? 9 : count;
  const petals = Array.from({ length: petalCount }, (_, index) => {
    const angle = (360 / petalCount) * index;
    return (
      <ellipse
        key={angle}
        cx="12"
        cy={count === "many" ? "5.8" : "6.5"}
        rx={count === "many" ? "1.45" : "2"}
        ry={count === "many" ? "3.1" : "3.7"}
        transform={`rotate(${angle} 12 12)`}
      />
    );
  });

  return (
    <>
      {petals}
      <circle cx="12" cy="12" r={count === "many" ? "2.2" : "1.8"} />
    </>
  );
}

function getSpecialTraitPaths(iconType) {
  switch (iconType) {
    case "persistence-evergreen":
      return (
        <path d="M12 21V7M12 9C8 5 5 5 3 6c1 4 4 6 9 6M12 14c4-4 7-4 9-3-1 4-4 6-9 6" />
      );
    case "persistence-deciduous":
      return (
        <path d="M7 20c4-4 6-8 7-14M14 6c3 0 5 1 6 3-3 2-5 1-6-3ZM7 15c-2-3-4-3-6-2 1 3 3 4 6 2ZM16 17l3 3m0-3-3 3" />
      );
    case "persistence-seasonal":
      return (
        <path d="M12 21V5M12 9 7 6M12 12l5-4M12 16l-4 3M7 6 5 3M17 8l2-3M3 21h18" />
      );

    case "shapeFlabellate":
      return (
        <>
          <path d="M12 21V13" />
          <path d="M12 13C8.2 12.4 5.2 10.5 3.3 7.4C5.9 4.5 8.8 3 12 3s6.1 1.5 8.7 4.4c-1.9 3.1-4.9 5-8.7 5.6Z" />
          <path strokeWidth="0.65" d="M12 13 5.2 7.4M12 13 8.4 4.4M12 13V3M12 13l3.6-8.6M12 13l6.8-5.6" />
        </>
      );

    case "apex-acute":
      return <path d="M5 21C6 13 8.5 6.5 12 2.5c3.5 4 6 10.5 7 18.5M12 3v18" />;
    case "apex-acuminate":
      return (
        <path d="M5 21C6 12 10.5 8.5 12 1.5c1.5 7 6 10.5 7 19.5M12 2v19" />
      );
    case "apex-obtuse":
      return (
        <path d="M5 21C5.8 12 7.5 6 9.5 4c1.3-1.2 3.7-1.2 5 0 2 2 3.7 8 4.5 17M12 4v17" />
      );
    case "apex-rounded":
      return <path d="M5 21C5.5 10 7.8 3 12 3s6.5 7 7 18M12 4v17" />;
    case "apex-emarginate":
      return (
        <path d="M5 21C5.8 12 6.5 6 9 3.5c1.2-1.2 2.4-.2 3 1.5.6-1.7 1.8-2.7 3-1.5 2.5 2.5 3.2 8.5 4 17.5M12 5v16" />
      );
    case "apex-mucronate":
      return <path d="M5 21C5.8 11 8 5 12 4.5c4 .5 6.2 6.5 7 16.5M12 1.5V21" />;

    case "base-cuneate":
      return <path d="M5 3c1.5 6.5 3.5 11 7 14 3.5-3 5.5-7.5 7-14M12 17v5" />;
    case "base-obtuse":
      return <path d="M5 3c.5 7 3 12 7 14 4-2 6.5-7 7-14M12 17v5" />;
    case "base-rounded":
      return <path d="M5 3v5c0 5 3 9 7 9s7-4 7-9V3M12 17v5" />;
    case "base-cordate":
      return (
        <path d="M5 3c0 6 1.5 11 5 13 1 .6 2-.2 2-2 0 1.8 1 2.6 2 2 3.5-2 5-7 5-13M12 14v8" />
      );
    case "base-truncate":
      return <path d="M5 3c0 5 1 9 2 13h10c1-4 2-8 2-13M7 16h10M12 16v6" />;
    case "base-oblique":
      return <path d="M5 3c0 8 2 13 7 14 3-4 5-8 7-14M12 17v5" />;
    case "base-attenuate":
      return (
        <path d="M5 3c1 5 3 9 5.5 12L12 19l1.5-4C16 12 18 8 19 3M12 19v3" />
      );

    case "venation-pinnate":
      return (
        <>
          {/* Exact same leaf outline used for Entire */}
          <path d="M20 4C12 4 6 7 4.5 12.5C3.5 16 6 19 9.5 19C15 19 18.5 13 20 4Z" />

          {/* Main midvein */}
          <path d="M5 20C8 15 12 11 17 8" />

          {/* Fine secondary veins branching from the midvein */}
          <g strokeWidth="0.75">
            {/* Toward upper/left margin */}
            <path d="M7.6 16.3L5.4 15.6" />
            <path d="M9.6 14.1L5.8 12.9" />
            <path d="M11.7 12L7.6 9.8" />
            <path d="M13.8 10.2L10.3 7.1" />
            <path d="M15.6 8.8L13.5 5.7" />

            {/* Toward lower/right margin */}
            <path d="M8.1 15.7L9.3 18.2" />
            <path d="M10.2 13.5L12.2 17.2" />
            <path d="M12.3 11.5L15 14.9" />
            <path d="M14.3 9.8L17 12.2" />
            <path d="M16 8.4L18.5 9.4" />
          </g>
        </>
      );
    case "venation-palmate":
      return (
        <>
          {/* Five-lobed leaf */}
          <path
            strokeWidth="0.9"
            d="
          M12 2.2

          C11.7 3.8 11 5.4 9.7 7.1
          C8.4 6.2 6.8 5.7 5 5.9
          C5.8 7.5 6.3 8.9 6.7 10.1

          C5.1 10.2 3.7 11.1 2.6 12.4
          C4.6 13.6 6.5 14.4 8.3 14.7
          C8.8 16 9.5 17 10.5 17.8
          C11 18.2 11.5 18.4 12 18.4

          C12.5 18.4 13 18.2 13.5 17.8
          C14.5 17 15.2 16 15.7 14.7
          C17.5 14.4 19.4 13.6 21.4 12.4

          C20.3 11.1 18.9 10.2 17.3 10.1
          C17.7 8.9 18.2 7.5 19 5.9
          C17.2 5.7 15.6 6.2 14.3 7.1

          C13 5.4 12.3 3.8 12 2.2
          Z
        "
          />

          {/* Petiole */}
          <path strokeWidth="0.9" d="M12 18.4V22.5" />

          {/* Five primary palmate veins from one common node */}
          <g strokeWidth="0.6">
            {/* Central lobe */}
            <path d="M12 18.4C12.1 13.7 12.2 7.2 12 2.2" />

            {/* Upper-left lobe */}
            <path d="M12 18.4C11.2 14.2 8.7 9.3 5 5.9" />

            {/* Lower-left lobe: rises well inside the blade before turning outward */}
            <path d="M12 18.4C11.8 16.6 11.2 15.1 10.2 14.1C8.4 13.1 5.4 12.6 2.6 12.4" />

            {/* Upper-right lobe */}
            <path d="M12 18.4C12.8 14.2 15.3 9.3 19 5.9" />

            {/* Lower-right lobe: rises well inside the blade before turning outward */}
            <path d="M12 18.4C12.2 16.6 12.8 15.1 13.8 14.1C15.6 13.1 18.6 12.6 21.4 12.4" />
          </g>
        </>
      );
    case "venation-parallel":
      return (
        <>
          {/* Long lanceolate blade */}
          <path
            strokeWidth="0.9"
            d="
          M12 2.2
          C8.3 4.4 5.9 8.9 6.2 13.7
          C6.4 16.8 8.6 18.7 12 19
          C15.4 18.7 17.6 16.8 17.8 13.7
          C18.1 8.9 15.7 4.4 12 2.2
          Z
        "
          />

          {/* Petiole */}
          <path strokeWidth="0.9" d="M12 19V22.5" />

          {/* Dominant central midvein */}
          <path strokeWidth="0.8" d="M12 19C12 13.4 12 7.5 12 2.4" />

          {/* Fine longitudinal veins running roughly parallel to the midvein */}
          <g strokeWidth="0.35">
            {/* Left side */}
            <path d="M10.9 18.6C9.7 14.4 9.6 8.8 11 3.5" />
            <path d="M9.7 18C8.3 13.8 8.4 8.8 10 4.5" />
            <path d="M8.6 17.1C7.3 13.2 7.6 9.3 9.2 5.8" />
            <path d="M7.7 15.8C6.8 12.5 7.2 9.8 8.6 7.2" />

            {/* Right side */}
            <path d="M13.1 18.6C14.3 14.4 14.4 8.8 13 3.5" />
            <path d="M14.3 18C15.7 13.8 15.6 8.8 14 4.5" />
            <path d="M15.4 17.1C16.7 13.2 16.4 9.3 14.8 5.8" />
            <path d="M16.3 15.8C17.2 12.5 16.8 9.8 15.4 7.2" />
          </g>
        </>
      );
    case "venation-arcuate":
      return (
        <path d="M12 21V3M12 18C7 16 5 12 5 7M12 14C8 12 7 9 7 5M12 18c5-2 7-6 7-11M12 14c4-2 5-5 5-9" />
      );
    case "venation-dichotomous":
      return (
        <path d="M12 21V12M12 12 8 8M12 12l4-4M8 8 5 4M8 8l1-5M16 8l-1-5M16 8l3-4" />
      );
    case "venation-triplinerved":
      return (
        <path d="M12 21V3M12 18C8 14 7 9 6 4M12 18c4-4 5-9 6-14M6 9 4 7M18 9l2-2M12 10 9 7M12 10l3-3" />
      );

    case "stipule-absent":
      return <path d="M12 21V4M12 10 7 7M12 10l5-3M5 15l14-6" />;
    case "stipule-free-lateral":
      return (
        <path d="M12 21V5M12 11 6 8M12 11l6-3M10.5 17l-3 2v-5l3 3ZM13.5 17l3 2v-5l-3 3Z" />
      );
    case "stipule-interpetiolar":
      return <path d="M12 21V5M12 11 5 8M12 11l7-3M9 12h6l-3 4-3-4Z" />;
    case "stipule-intrapetiolar":
      return (
        <path d="M12 21V5M12 11 5 8M12 11l7-3M10 10c0 4 1 6 2 7 1-1 2-3 2-7" />
      );
    case "stipule-adnate":
      return (
        <path d="M12 21V5M12 11 5 8M12 11l7-3M10 18c0-4 .5-7 2-9M14 18c0-4-.5-7-2-9" />
      );
    case "stipule-ochreate":
      return <path d="M12 21V4M8.5 16h7l-1-7h-5l-1 7ZM9.5 9h5" />;
    case "stipule-foliaceous":
      return (
        <path d="M12 21V5M12 11 5 8M12 11l7-3M10 16c-3-2-5-1-6 2 3 1 5 0 6-2ZM14 16c3-2 5-1 6 2-3 1-5 0-6-2Z" />
      );
    case "stipule-spinescent":
      return <path d="M12 21V5M12 11 5 8M12 11l7-3M10 16 5 12M14 16l-5-4" />;
    case "stipule-caducous":
      return (
        <path d="M12 21V5M12 11 5 8M12 11l7-3M9 16h6M8 18l2 2M16 18l-2 2" />
      );
    case "stipule-inconspicuous":
      return <path d="M12 21V5M12 11 5 8M12 11l7-3M10.5 16l-1 1M13.5 16l1 1" />;

    case "petiole-length-sessile":
      return (
        <>
          {/* Sessile: blade attaches directly, with no petiole */}
          <path d="M12 21C8.2 18.8 6 14.6 6.4 9.8C6.8 5.8 9 3 12 2C15 3 17.2 5.8 17.6 9.8C18 14.6 15.8 18.8 12 21Z" />
        </>
      );

    case "petiole-length-subsessile":
      return (
        <>
          {/* Same basic blade */}
          <path d="M12 19.5C8.2 17.3 6 13.3 6.4 8.7C6.8 5.1 9 2.7 12 2C15 2.7 17.2 5.1 17.6 8.7C18 13.3 15.8 17.3 12 19.5Z" />

          {/* Extremely short petiole */}
          <path d="M12 19.5V22" />
        </>
      );

    case "petiole-length-short":
      return (
        <>
          <path d="M12 17.5C8.5 15.5 6.5 11.9 6.8 7.9C7.2 4.7 9.2 2.5 12 2C14.8 2.5 16.8 4.7 17.2 7.9C17.5 11.9 15.5 15.5 12 17.5Z" />

          {/* Short petiole */}
          <path d="M12 17.5V22" />
        </>
      );

    case "petiole-length-medium":
      return (
        <>
          <path d="M12 15C8.8 13.2 7 10 7.3 6.7C7.7 4 9.4 2.2 12 1.8C14.6 2.2 16.3 4 16.7 6.7C17 10 15.2 13.2 12 15Z" />

          {/* Medium petiole */}
          <path d="M12 15V22" />
        </>
      );

    case "petiole-length-long":
      return (
        <>
          <path d="M12 11.5C9.4 10 8 7.7 8.3 5.2C8.6 3.1 10 1.8 12 1.5C14 1.8 15.4 3.1 15.7 5.2C16 7.7 14.6 10 12 11.5Z" />

          {/* Long petiole */}
          <path d="M12 11.5V22" />
        </>
      );
    case "petiole-form-terete":
      return <path d="M12 22V8M9 8h6M10 5a2 2 0 1 0 4 0 2 2 0 1 0-4 0" />;
    case "petiole-form-canaliculate":
      return <path d="M9 22c1-8 1-12 3-17 2 5 2 9 3 17M12 5V2M10 8h4" />;
    case "petiole-form-flattened":
      return <path d="M10 22V7h4v15M9 7h6M10 3h4" />;
    case "petiole-form-winged":
      return (
        <path d="M12 22V5M12 9c-3 0-5 2-6 5 3 0 5-2 6-5ZM12 12c3 0 5 2 6 5-3 0-5-2-6-5Z" />
      );
    case "petiole-form-pulvinate":
      return (
        <path d="M12 22V8M9 8c0-2 1-3 3-3s3 1 3 3-1 3-3 3-3-1-3-3ZM12 5V2" />
      );

    case "flower-solitary":
      return (
        <>
          <PetalPaths count={5} />
          <path d="M12 14v8" />
        </>
      );
    case "flower-cluster":
      return (
        <>
          <path d="M12 22V9M12 15 7 11M12 16l5-5" />
          <circle cx="12" cy="6" r="2.3" />
          <circle cx="6" cy="9" r="2.3" />
          <circle cx="18" cy="9" r="2.3" />
        </>
      );
    case "inflorescence-spike":
      return (
        <path d="M12 22V3M12 6 9 5M12 9l3-1M12 12l-3-1M12 15l3-1M12 18l-3-1" />
      );
    case "inflorescence-raceme":
      return (
        <>
          <path d="M12 22V3M12 7 8 5M12 10l4-2M12 13l-4-2M12 16l4-2" />
          <circle cx="7" cy="4" r="1.6" />
          <circle cx="17" cy="7" r="1.6" />
          <circle cx="7" cy="10" r="1.6" />
          <circle cx="17" cy="13" r="1.6" />
        </>
      );
    case "inflorescence-panicle":
      return (
        <>
          <path d="M12 22V5M12 11 7 7M12 14l6-5M7 7 5 4M18 9l2-4" />
          <circle cx="12" cy="3" r="1.4" />
          <circle cx="5" cy="6" r="1.4" />
          <circle cx="9" cy="4" r="1.4" />
          <circle cx="20" cy="4" r="1.4" />
          <circle cx="17" cy="7" r="1.4" />
        </>
      );
    case "inflorescence-umbel":
      return (
        <>
          <path d="M12 22V12M12 12 5 5M12 12 9 3M12 12l3-9M12 12l7-7" />
          <circle cx="5" cy="4" r="1.5" />
          <circle cx="9" cy="2.5" r="1.5" />
          <circle cx="15" cy="2.5" r="1.5" />
          <circle cx="19" cy="4" r="1.5" />
        </>
      );
    case "inflorescence-corymb":
      return (
        <>
          <path d="M12 22V16M12 16 5 7M12 16 9 7M12 16l3-9M12 16l7-9" />
          <circle cx="5" cy="6" r="1.5" />
          <circle cx="9" cy="6" r="1.5" />
          <circle cx="15" cy="6" r="1.5" />
          <circle cx="19" cy="6" r="1.5" />
        </>
      );
    case "inflorescence-head":
      return (
        <>
          <path d="M12 22v-8" />
          <circle cx="12" cy="8" r="6" />
          <circle cx="9" cy="6" r="1" />
          <circle cx="14" cy="5" r="1" />
          <circle cx="15" cy="10" r="1" />
          <circle cx="10" cy="11" r="1" />
        </>
      );
    case "inflorescence-cyme":
      return (
        <>
          <path d="M12 22V8M12 14 6 9M12 14l6-5M6 9l-2-4M6 9l3-5M18 9l-3-5M18 9l2-4" />
          <circle cx="12" cy="5" r="1.6" />
          <circle cx="4" cy="4" r="1.4" />
          <circle cx="9" cy="3" r="1.4" />
          <circle cx="15" cy="3" r="1.4" />
          <circle cx="20" cy="4" r="1.4" />
        </>
      );
    case "inflorescence-catkin":
      return (
        <>
          <path d="M8 2c0 4 1 7 4 9M12 11c4 3 4 8 1 11" />
          <path d="m9 5 3-1M10 8l3-1M12 13l3-1M13 16l3-1M13 19l2-1" />
        </>
      );
    case "inflorescence-spadix":
      return (
        <>
          <path d="M12 22V7c0-4 4-4 4 0v8c0 3-2 5-4 5" />
          <path d="M11 18C5 16 4 10 7 5c4 1 6 5 4 13Z" />
          <circle cx="14" cy="7" r=".6" />
          <circle cx="14" cy="10" r=".6" />
          <circle cx="14" cy="13" r=".6" />
        </>
      );

    case "symmetry-radial":
      return <PetalPaths count={5} />;
    case "symmetry-bilateral":
      return (
        <>
          <path d="M12 3c-3 0-5 2-5 5 0 2 1 3 3 4-3 1-4 3-3 6 2 3 8 3 10 0 1-3 0-5-3-6 2-1 3-2 3-4 0-3-2-5-5-5Z" />
          <path d="M12 3v18" />
        </>
      );
    case "symmetry-bilateral-resupinate":
      return (
        <>
          <path d="M12 4c-3-3-6 0-4 3-4-1-6 3-3 5-2 4 3 7 7 4 4 3 9 0 7-4 3-2 1-6-3-5 2-3-1-6-4-3Z" />
          <path d="M12 4v12M9 19h6" />
        </>
      );
    case "symmetry-asymmetric":
      return (
        <>
          <path d="M11 4c-4-2-7 2-4 5-5 1-4 6 0 6-1 5 5 7 7 3 5 1 6-4 2-6 1-4-1-7-4-6 0-3-3-5-4-2Z" />
          <path d="M5 20 19 4" />
        </>
      );
    case "sexuality-perfect":
      return (
        <>
          <PetalPaths count={5} />
          <circle cx="12" cy="12" r=".7" />
          <path d="M12 8v8M9 10l6 4M15 10l-6 4" />
        </>
      );

    case "sexuality-monoecious":
      return (
        <>
          {/* Male and female flowers on the same plant */}
          <path d="M12 22V8M12 13L7 9M12 15L17 10" />
          <circle cx="7" cy="7" r="2.3" />
          <path d="M8.6 5.4L10.5 3.5M9 3.5h1.5V5" />
          <circle cx="17" cy="8" r="2.3" />
          <path d="M17 10.3V13M15.5 11.7h3" />
        </>
      );

    case "sexuality-dioecious":
      return (
        <>
          {/* Male and female symbols separated between individuals */}
          <circle cx="7" cy="9" r="3" />
          <path d="M9.2 6.8L12 4M10 4h2v2" />

          <circle cx="17" cy="15" r="3" />
          <path d="M17 18V21M15.5 19.5h3" />

          <path strokeWidth="0.7" d="M12 5L12 20" />
        </>
      );

    case "sexuality-polygamous":
      return (
        <>
          {/* Combination of perfect and unisexual flowers */}
          <circle cx="12" cy="11" r="2" />
          <path d="M12 9c-2.8-3 1.8-5 3-2 .5 1.5-.8 3-3 4M14 11c3-2 5 1.5 2.5 3-1.5.7-3-.3-4.5-3M10 11c-3 2-5-1.5-2.5-3 1.5-.7 3 .3 4.5 3" />

          <circle cx="5" cy="17" r="2" />
          <path d="M6.5 15.5L8.5 13.5M7 13.5h1.5V15" />

          <circle cx="19" cy="17" r="2" />
          <path d="M19 19V21M17.8 20h2.4" />
        </>
      );
    case "sexuality-unisexual":
      return (
        <>
          <circle cx="8" cy="9" r="4" />
          <path d="M11 6l4-4M12 2h3v3M16 19a4 4 0 1 0-8 0 4 4 0 1 0 8 0ZM12 15v-3M10 13h4" />
        </>
      );
    case "sexuality-both":
      return (
        <>
          <circle cx="8" cy="8" r="3" />
          <path d="M10 6l3-3M11 3h2v2M15 19a3 3 0 1 0-6 0 3 3 0 1 0 6 0ZM12 16v-3M10 14h4" />
        </>
      );
    case "petals-absent":
      return <PetalPaths count={0} />;
    case "petals-three":
      return <PetalPaths count={3} />;
    case "petals-four":
      return <PetalPaths count={4} />;
    case "petals-five":
      return <PetalPaths count={5} />;
    case "petals-six":
      return <PetalPaths count={6} />;
    case "petals-many":
      return <PetalPaths count="many" />;
    case "ovary-superior":
      return (
        <path d="M5 10h14M8 10c0 5 2 8 4 8s4-3 4-8M7 7l2 3M17 7l-2 3M12 18v4" />
      );
    case "ovary-inferior":
      return (
        <path d="M5 15h14M8 15c0-5 2-8 4-8s4 3 4 8M7 18l2-3M17 18l-2-3M12 7V2" />
      );
    case "ovary-half-inferior":
      return (
        <path d="M5 12h14M8 12c0-3 2-6 4-6s4 3 4 6c0 4-2 7-4 7s-4-3-4-7ZM7 9l2 3M17 9l-2 3" />
      );

    case "fruit-berry":
      return (
        <>
          <circle cx="12" cy="13" r="7" />
          <path d="M12 6V2M12 5c3-2 5-1 6 1-3 1-5 0-6-1Z" />
          <circle cx="10" cy="12" r=".7" />
          <circle cx="14" cy="15" r=".7" />
        </>
      );
    case "fruit-drupe":
      return (
        <>
          <path d="M12 4c-5 0-8 4-7 9 1 6 4 8 7 8s6-2 7-8c1-5-2-9-7-9Z" />
          <ellipse cx="12" cy="13" rx="2.5" ry="4" />
          <path d="M12 4V2" />
        </>
      );
    case "fruit-capsule":
      return (
        <>
          <path d="M8 6c-2 5-2 10 1 15h6c3-5 3-10 1-15l-4-3-4 3Z" />
          <path d="M12 3v18M8 10h8M8 6l-3-2M16 6l3-2" />
        </>
      );
    case "fruit-legume":
      return (
        <>
          <path d="M5 19C13 20 19 14 19 5 11 4 5 10 5 19Z" />
          <circle cx="9" cy="16" r=".7" />
          <circle cx="12" cy="13" r=".7" />
          <circle cx="15" cy="9" r=".7" />
        </>
      );
    case "fruit-follicle":
      return (
        <>
          <path d="M7 20c7-1 11-7 10-16-6 2-10 8-10 16Z" />
          <path d="M8 19 16 6M10 15l3 1M12 11l3 1" />
        </>
      );
    case "fruit-achene":
      return (
        <>
          <path d="M12 5c-4 3-5 9-2 15h4c3-6 2-12-2-15Z" />
          <path d="M12 5V2M9 4l3-2 3 2" />
        </>
      );
    case "fruit-caryopsis":
      return (
        <>
          <path d="M12 3C7 8 7 15 12 21c5-6 5-13 0-18Z" />
          <path d="M12 5v14M9 8l3 3M15 8l-3 3" />
        </>
      );
    case "fruit-nut":
      return (
        <>
          <path d="M7 10c0 7 2 11 5 11s5-4 5-11H7Z" />
          <path d="M6 10c1-4 3-6 6-6s5 2 6 6H6ZM12 4V2" />
        </>
      );
    case "fruit-samara":
      return (
        <>
          <circle cx="7" cy="17" r="2.2" />
          <path d="M9 16C15 15 19 10 19 4c-6 1-10 5-10 12Z" />
          <path d="M10 15 18 6" />
        </>
      );
    case "fruit-schizocarp":
      return (
        <>
          <path d="M12 4v17M12 8C8 3 4 5 5 10c1 4 4 6 7 5M12 8c4-5 8-3 7 2-1 4-4 6-7 5" />
          <path d="M9 18h6" />
        </>
      );
    case "fruit-silique":
      return (
        <>
          <path d="M9 3c-2 6-2 12 0 18M15 3c2 6 2 12 0 18M9 3h6M9 21h6M12 3v18" />
          <circle cx="11" cy="7" r=".5" />
          <circle cx="13" cy="11" r=".5" />
          <circle cx="11" cy="15" r=".5" />
        </>
      );
    case "fruit-pome":
      return (
        <>
          <path d="M12 7C7 4 4 8 5 14c1 5 4 7 7 5 3 2 6 0 7-5 1-6-2-10-7-7Z" />
          <path d="M12 7V3M12 4c3-2 5-1 6 1-3 1-5 0-6-1ZM12 11l2 3-2 3-2-3 2-3Z" />
        </>
      );
    case "fruit-pepo":
      return (
        <>
          <ellipse cx="12" cy="14" rx="8" ry="6" />
          <path d="M12 8V3M9 9c-2 3-2 7 0 10M15 9c2 3 2 7 0 10M12 9v10" />
        </>
      );
    case "fruit-hesperidium":
      return (
        <>
          <circle cx="12" cy="13" r="8" />
          <path d="M12 5v16M4 13h16M6.5 7.5l11 11M17.5 7.5l-11 11M12 5V2" />
        </>
      );
    case "fruit-cone":
      return (
        <>
          <path d="M12 3c-5 5-6 11-3 18h6c3-7 2-13-3-18Z" />
          <path d="m9 8 6 4M15 8l-6 4M9 14l6 4M15 14l-6 4" />
        </>
      );
    case "fruit-aggregate":
      return (
        <>
          <circle cx="12" cy="12" r="2" />
          <circle cx="12" cy="7" r="2.5" />
          <circle cx="8" cy="10" r="2.5" />
          <circle cx="16" cy="10" r="2.5" />
          <circle cx="9" cy="15" r="2.5" />
          <circle cx="15" cy="15" r="2.5" />
          <path d="M12 5V2M12 18v4" />
        </>
      );
    case "fruit-multiple":
      return (
        <>
          <path d="M12 3c-4 2-7 7-6 12 1 4 3 6 6 6s5-2 6-6c1-5-2-10-6-12Z" />
          <path d="M8 8h8M7 12h10M7 16h10M9 5l6 14M15 5 9 19" />
        </>
      );
    case "fruit-texture-fleshy":
      return (
        <>
          <circle cx="12" cy="13" r="7" />
          <path d="M12 6V2M8 13c2 2 6 2 8 0" />
        </>
      );
    case "fruit-texture-dry":
      return (
        <>
          <path d="M12 3c-5 5-6 11-3 18h6c3-7 2-13-3-18Z" />
          <path d="M9 9h6M9 14h6" />
        </>
      );
    case "fruit-opening-dehiscent":
      return (
        <>
          <path d="M12 3c-4 5-5 11-3 18M12 3c4 5 5 11 3 18M12 3v18" />
          <path d="m9 15-3 3M15 15l3 3" />
        </>
      );
    case "fruit-opening-indehiscent":
      return (
        <>
          <path d="M12 3c-5 5-6 11-3 18h6c3-7 2-13-3-18Z" />
          <path d="M10 12h4" />
        </>
      );
    default:
      return null;
  }
}

function TraitGlyph({ fieldKey, value, className = "h-4 w-4" }) {
  const iconType = getTraitIconType(fieldKey, value);
  const specialPaths = getSpecialTraitPaths(iconType);
  let paths;

  if (specialPaths) {
    return (
      <svg
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.2"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
        className={className}
      >
        {specialPaths}
      </svg>
    );
  }

  switch (iconType) {
    case "tree":
      paths = (
        <>
          <path d="M12 21v-6" />
          <path d="M9.5 21h5" />
          <path d="M12 3c-3.6 0-6 3-4.8 5.8A4 4 0 0 0 9 16h6a4 4 0 0 0 1.8-7.2C18 6 15.6 3 12 3Z" />
        </>
      );
      break;
    case "shrub":
      paths = (
        <>
          {/* Rounded, dense shrub canopy */}
          <path
            d="
          M5.2 20
          C3.8 19.9 2.8 18.9 3.1 17.6
          C1.9 17 1.6 15.4 2.5 14.3
          C1.4 13.3 1.5 11.3 2.9 10.2
          C2.3 8.5 3.4 6.7 5.2 6.3
          C6.2 6.1 7.2 6.4 7.9 7
          C7.4 5.1 8.8 3.1 10.8 2.6
          C13.1 2 15.3 3.5 15.5 5.8
          C17 4.9 19 5.3 19.8 6.9
          C20.5 8.2 20.1 9.6 19.2 10.4
          C21.1 10.4 22.5 12 22.5 13.9
          C22.5 15.6 21.5 16.9 20 17.3
          C20.8 18.5 20 19.9 18.6 20
          C17.8 21.1 16.1 21.2 15.2 20.3
          C14.1 21.3 12.4 21.2 11.5 20.2
          C10.4 21.2 8.8 21.2 7.9 20.2
          C7 20.8 5.9 20.7 5.2 20
        "
          />

          {/* Main central woody stem */}
          <path d="M12 21V14L9.2 12L8.5 10" />

          {/* Upper branch */}
          <path d="M12 12.7L13.5 9.4V7" />

          {/* Left basal stems */}
          <path d="M8.8 21L6.2 16.8" />
          <path d="M8.8 21L8.9 16" />

          {/* Right basal stems */}
          <path d="M15.3 21L14.4 15.2" />
          <path d="M15.3 21L17.8 16.5" />
          <path d="M15.3 18L16.4 13.5" />

          {/* Ground line */}
          <path d="M2 21H22" />
        </>
      );
      break;
    case "herb":
      paths = (
        <>
          <path d="M12 21V9" />
          <path d="M12 13c-4 0-6-2-6-5 4 0 6 2 6 5ZM12 17c4 0 6-2 6-5-4 0-6 2-6 5Z" />
        </>
      );
      break;
    case "vine":
    case "liana":
      paths = (
        <>
          {/* Long, sinuous climbing stem */}
          <path d="M3.5 22C5.7 19.2 5.3 16.8 8.2 14.2C10.9 11.8 10.2 9.5 13.2 7.3C15.4 5.7 16.4 4.3 17.8 3.2" />

          {/* Short petioles connecting leaves to stem */}
          <path d="M6.1 17.4L4.8 16M8.3 14.1L10.1 13M11.4 9.8L10.1 8.4M14.4 6.5L16.1 5.7" />

          {/* Lower left leaf */}
          <path d="M4.8 16C3.2 15.8 2.3 14.6 2.5 13.1C4.1 13.2 5.1 14.4 4.8 16Z" />

          {/* Lower right leaf */}
          <path d="M10.1 13C11.8 12.4 13.3 12.9 14.1 14.1C12.6 15 10.9 14.6 10.1 13Z" />

          {/* Upper left leaf */}
          <path d="M10.1 8.4C8.7 7.9 8 6.6 8.4 5.1C10 5.3 10.9 6.7 10.1 8.4Z" />

          {/* Upper right leaf */}
          <path d="M16.1 5.7C17.4 4.5 19.2 4.4 20.4 5.2C19.4 6.6 17.5 7 16.1 5.7Z" />

          {/* Terminal leaf */}
          <path d="M17.8 3.2C17.1 2.1 17.3 1 18.2.3C19.3 1.1 19.5 2.4 18.7 3.5Z" />
        </>
      );
      break;
    case "epiphyte":
      paths = (
        <>
          {" "}
          {/* Vertical tree trunk / substrate */} <path d="M4.5 2V22" />{" "}
          {/* Small clasping base attached to trunk */}{" "}
          <path d="M4.5 12.5C5.3 11.8 6 11.7 6.8 12.4" />{" "}
          <path d="M4.5 14C5.4 14.6 6.1 14.5 6.8 13.8" />{" "}
          {/* Bromeliad rosette: leaves radiating from lateral attachment point */}{" "}
          <path d="M6.2 13C8.1 10.3 10.6 8.6 13.8 7.4C12.5 10.2 10.1 12 6.2 13Z" />{" "}
          <path d="M6.4 13C9.4 11.7 12.5 11.5 16.4 12.2C13.8 14 10.5 14.5 6.4 13Z" />{" "}
          <path d="M6.3 13.2C9.3 14.1 11.9 15.8 14.3 18.5C11.1 18 8.4 16.2 6.3 13.2Z" />{" "}
          {/* Narrower upper leaves */}{" "}
          <path d="M6.2 12.8C7.3 9.6 8.8 7.2 10.8 5.5C10.7 8.7 9.2 11.2 6.2 12.8Z" />{" "}
          <path d="M6.5 13C9.5 9.7 12.1 8.1 15.1 7.8C13.7 10.7 10.8 12.5 6.5 13Z" />{" "}
          {/* Lower leaf */}{" "}
          <path d="M6.3 13.4C8.3 16 9.2 18.4 9.1 20.4C7.1 18.8 6.1 16.4 6.3 13.4Z" />{" "}
          {/* Short aerial roots gripping the trunk */}{" "}
          <path d="M5.6 14.1C5.1 15.4 5.2 16.5 5.8 17.3" />{" "}
          <path d="M6.2 14C6 15.5 6.5 16.5 7.2 17.2" />{" "}
        </>
      );
      break;
    case "palm":
      paths = (
        <>
          {/* =========================================================
          TRUNK
          Slightly curved, tapering upward, with old leaf-scar bands.
          ========================================================= */}

          {/* Left and right edges of trunk */}
          <path d="M10.5 22C10.7 18.4 11 14.2 12.1 9" />
          <path d="M13.8 22C13.7 18.2 13.7 14.1 13 9" />

          {/* Base */}
          <path d="M9.8 22H14.5" />

          {/* Horizontal / diagonal leaf scars on trunk */}
          <path d="M10.8 19.5L13.6 19.8" />
          <path d="M10.9 17.2L13.5 17.5" />
          <path d="M11.2 14.9L13.3 15.2" />
          <path d="M11.6 12.7L13.2 13" />
          <path d="M11.9 10.8L13.1 11" />

          {/* =========================================================
          CROWN / GROWING POINT
          ========================================================= */}

          <path d="M11.8 9C12.1 8.4 12.6 8.1 13.1 8.2C13.6 8.4 13.9 8.8 14 9.3" />

          {/* =========================================================
          FROND 1 - tall central frond
          ========================================================= */}

          <path d="M12.9 8.7C12.6 6.3 13.1 3.8 14.8 1.5" />

          {/* Leaflets */}
          <path d="M13.2 6.8L12 5.8" />
          <path d="M13.4 5.7L12.4 4.6" />
          <path d="M13.7 4.7L12.9 3.6" />
          <path d="M13.2 6.2L14.8 5.5" />
          <path d="M13.5 5.1L15.2 4.4" />
          <path d="M13.9 4L15.5 3.2" />

          {/* =========================================================
          FROND 2 - upper left
          ========================================================= */}

          <path d="M12.7 8.8C10.7 6.5 8.5 4.8 5.8 3.5" />

          {/* Leaflets */}
          <path d="M10.8 6.8L10.2 5.2" />
          <path d="M9.7 5.8L8.9 4.3" />
          <path d="M8.5 5L7.7 3.7" />
          <path d="M10.1 6.2L8.7 6.4" />
          <path d="M8.9 5.3L7.4 5.5" />
          <path d="M7.7 4.6L6.4 4.8" />

          {/* =========================================================
          FROND 3 - upper right
          ========================================================= */}

          <path d="M13.1 8.8C15.6 6.5 18.1 5.3 21.2 5.5" />

          {/* Leaflets */}
          <path d="M15.1 7.2L15.5 5.7" />
          <path d="M16.4 6.4L17 5" />
          <path d="M17.7 5.9L18.5 4.8" />
          <path d="M15.8 6.7L17.1 7.1" />
          <path d="M17.2 6.1L18.7 6.5" />
          <path d="M18.6 5.7L20 6.2" />

          {/* =========================================================
          FROND 4 - broad drooping left frond
          ========================================================= */}

          <path d="M12.6 9C9.6 8.3 6.4 8.8 3.3 11.4" />

          {/* Leaflets */}
          <path d="M10.3 8.7L9.5 7.3" />
          <path d="M8.8 8.7L7.9 7.4" />
          <path d="M7.3 9L6.2 7.9" />
          <path d="M5.9 9.6L4.7 8.8" />

          <path d="M9.7 8.9L9 10.5" />
          <path d="M8.1 9L7.3 10.8" />
          <path d="M6.5 9.5L5.6 11.2" />
          <path d="M5.1 10.2L4.2 11.7" />

          {/* =========================================================
          FROND 5 - broad drooping right frond
          ========================================================= */}

          <path d="M13.2 9C16.4 8.5 19.4 9.7 21.8 12.4" />

          {/* Leaflets */}
          <path d="M15.6 9L16.1 7.5" />
          <path d="M17.1 9.3L17.8 7.9" />
          <path d="M18.6 9.8L19.4 8.6" />
          <path d="M20 10.6L20.9 9.6" />

          <path d="M15.9 9.2L15.8 10.9" />
          <path d="M17.5 9.6L17.4 11.3" />
          <path d="M19 10.2L18.9 11.9" />
          <path d="M20.4 11L20.2 12.5" />

          {/* =========================================================
          FROND 6 - rear left, upward
          Gives the crown more radial fullness.
          ========================================================= */}

          <path d="M12.8 8.7C11.2 6.3 10.2 4.2 10 2.3" />

          <path d="M11.5 6.5L10.2 5.7" />
          <path d="M10.9 5.3L9.8 4.5" />
          <path d="M10.5 4L9.5 3.4" />

          <path d="M11.2 5.9L12 4.8" />
          <path d="M10.7 4.7L11.5 3.7" />

          {/* =========================================================
          FROND 7 - rear right, downward arch
          ========================================================= */}

          <path d="M13.2 9C15.3 10 16.9 11.8 17.7 14" />

          <path d="M14.7 9.9L15.9 9.4" />
          <path d="M15.7 10.8L17 10.5" />
          <path d="M16.5 11.8L17.8 11.6" />

          <path d="M15 10.2L14.9 11.6" />
          <path d="M16 11.1L15.9 12.6" />
          <path d="M16.8 12.2L16.6 13.6" />
        </>
      );
      break;
    case "grass":
    case "sedge":
      paths = (
        <>
          <path d="M12 21V5M12 21c-1-7-4-10-7-12M12 21c2-7 5-10 8-12M12 21c-4-4-6-5-9-5M12 21c4-4 6-5 9-5" />
        </>
      );
      break;
    case "fern":
      paths = (
        <>
          {/* Main curved rachis */}
          <path d="M6.5 21C8.2 17.3 10.2 13.8 12.7 10.2C14.7 7.3 16.6 5 18.6 3" />

          {/* Lowest pair - longest */}
          <path d="M8.2 17.5C6.2 17.2 4.5 16.2 3.4 14.8C5.4 14.7 7.2 15.5 8.2 17.5Z" />
          <path d="M8.5 16.9C10.5 17.1 12.3 18 13.8 19.3C11.7 19.5 9.7 18.7 8.5 16.9Z" />

          {/* Second pair */}
          <path d="M9.8 14.9C8 14.6 6.5 13.8 5.4 12.6C7.2 12.5 8.8 13.2 9.8 14.9Z" />
          <path d="M10.1 14.3C11.9 14.5 13.5 15.2 14.9 16.4C13 16.6 11.3 15.9 10.1 14.3Z" />

          {/* Third pair */}
          <path d="M11.4 12.4C9.9 12.1 8.6 11.4 7.7 10.3C9.3 10.2 10.6 10.8 11.4 12.4Z" />
          <path d="M11.7 11.8C13.2 12 14.6 12.6 15.7 13.5C14.1 13.8 12.7 13.2 11.7 11.8Z" />

          {/* Fourth pair */}
          <path d="M13.1 9.9C11.8 9.6 10.8 9 10 8.1C11.3 8 12.4 8.6 13.1 9.9Z" />
          <path d="M13.4 9.4C14.7 9.5 15.8 10 16.8 10.8C15.4 11 14.2 10.5 13.4 9.4Z" />

          {/* Fifth pair - smallest */}
          <path d="M14.8 7.6C13.8 7.3 13 6.8 12.5 6.1C13.5 6 14.3 6.5 14.8 7.6Z" />
          <path d="M15.1 7.2C16.1 7.3 17 7.7 17.7 8.3C16.6 8.5 15.7 8.1 15.1 7.2Z" />

          {/* Terminal pinna */}
          <path d="M16.5 5.4C17.1 4.4 17.8 3.6 18.6 3C18.8 4.1 18.1 5 16.5 5.4Z" />
        </>
      );
      break;
    case "aquatic":
      paths = (
        <>
          {/* Lily pad outline: rounded disk, small upper notch, deep lower cleft */}
          <path
            d="
          M12 4
          C10.8 2.9 9.7 2.5 8.3 2.8
          C5.2 3.4 2.8 6.7 2.6 11
          C2.4 15.4 4.6 19.1 8.1 20.5
          C9.1 20.9 9.8 20.7 10.2 19.8
          C11 18 11.5 15.8 11.7 13.4
          C11.8 12.7 11.9 12.3 12 12
          C12.1 12.3 12.2 12.7 12.3 13.4
          C12.5 15.8 13 18 13.8 19.8
          C14.2 20.7 14.9 20.9 15.9 20.5
          C19.4 19.1 21.6 15.4 21.4 11
          C21.2 6.7 18.8 3.4 15.7 2.8
          C14.3 2.5 13.2 2.9 12 4
          Z
        "
          />

          {/* Central vein */}
          <path d="M12 12V6.5" />

          {/* Upper-left veins */}
          <path d="M11.6 10.5C10.7 9.3 9.8 7.8 9.1 6.2" />
          <path d="M11.1 11C9.4 9.7 7.8 8.4 6.6 7.2" />

          {/* Left lateral veins */}
          <path d="M10.8 11.6C8.7 11.1 6.6 10.7 4.5 10.4" />
          <path d="M10.9 12.3C8.6 13 6.5 14.1 4.8 15.2" />

          {/* Lower-left vein */}
          <path d="M11.3 12.7C10 14.8 8.9 16.7 7.9 18.2" />

          {/* Upper-right veins */}
          <path d="M12.4 10.5C13.3 9.3 14.2 7.8 14.9 6.2" />
          <path d="M12.9 11C14.6 9.7 16.2 8.4 17.4 7.2" />

          {/* Right lateral veins */}
          <path d="M13.2 11.6C15.3 11.1 17.4 10.7 19.5 10.4" />
          <path d="M13.1 12.3C15.4 13 17.5 14.1 19.2 15.2" />

          {/* Lower-right vein */}
          <path d="M12.7 12.7C14 14.8 15.1 16.7 16.1 18.2" />
        </>
      );
      break;
    case "creeping":
      paths = (
        <>
          {/* Long, gently sinuous horizontal stem */}
          <path d="M2.5 14.5C5.2 13.2 7.1 14.2 9.4 13.6C11.8 13 13.5 13.8 15.8 13.1C18 12.5 19.7 13 21.5 12.4" />

          {/* Petioles */}
          <path d="M5.6 13.9L4.6 12.5" />
          <path d="M9.2 13.6L10.2 12.2" />
          <path d="M13.4 13.4L12.4 11.9" />
          <path d="M17.2 12.8L18.3 11.5" />

          {/* Alternating leaves */}
          <path d="M4.6 12.5C3.2 12.2 2.3 11.2 2.5 10C3.9 10.1 4.8 11.1 4.6 12.5Z" />
          <path d="M10.2 12.2C11.6 11.8 12.8 12.2 13.4 13.2C12.1 13.9 10.9 13.6 10.2 12.2Z" />
          <path d="M12.4 11.9C11 11.5 10.2 10.5 10.4 9.3C11.8 9.4 12.6 10.4 12.4 11.9Z" />
          <path d="M18.3 11.5C19.6 11 20.8 11.3 21.5 12.2C20.3 13 19 12.8 18.3 11.5Z" />

          {/* Small rooting points at nodes */}
          <path d="M7.7 13.9C7.6 15 7.3 16 6.7 16.8" />
          <path d="M15.5 13.2C15.4 14.2 15.1 15.1 14.5 15.8" />
        </>
      );
      break;
    case "trailing":
      paths = (
        <>
          {/* Hanging plant pot */}
          <path d="M5.5 4.5H15.5" />
          <path d="M6.5 4.5L7.3 8.3H13.7L14.5 4.5" />

          {/* Main cascading stem */}
          <path d="M11.5 8.3C12 10.5 10.8 12.2 11.4 14.2C12 16.2 10.8 18.3 9.2 21.5" />

          {/* Secondary shorter trailing stem */}
          <path d="M9 8.3C8.6 10.2 7.5 11.6 6.3 13.2" />

          {/* Petioles */}
          <path d="M11.1 12L13 11.2" />
          <path d="M11.3 15.3L9.2 14.7" />
          <path d="M10.5 18.2L12.3 17.5" />
          <path d="M7.4 11.8L5.8 10.9" />

          {/* Pothos-like leaves */}
          <path d="M13 11.2C14.3 9.8 16.2 10.2 16.5 11.7C16.7 13.1 15.2 14.2 13.2 14C13.3 12.8 13.3 12 13 11.2Z" />

          <path d="M9.2 14.7C7.8 13.4 6.1 13.8 5.9 15.3C5.8 16.7 7.3 17.8 9.2 17.4C9 16.3 9 15.5 9.2 14.7Z" />

          <path d="M12.3 17.5C13.6 16.2 15.2 16.6 15.5 18C15.7 19.3 14.3 20.3 12.5 20C12.6 19 12.6 18.2 12.3 17.5Z" />

          <path d="M5.8 10.9C4.7 9.8 3.3 10.1 3.1 11.4C3 12.6 4.2 13.5 5.8 13.2C5.7 12.3 5.7 11.6 5.8 10.9Z" />
        </>
      );
      break;
    case "rosetteHabit":
      paths = (
        <>
          {/* Central crown */}
          <path d="M12 18C11.3 14.8 11.5 11.4 12.3 7.7C13.5 11.5 13.3 14.8 12 18Z" />

          {/* Tall inner left leaf */}
          <path d="M11.5 17.8C9.7 14.7 9 11.7 9.4 9C11.4 11.4 12.1 14.2 11.5 17.8Z" />

          {/* Tall inner right leaf */}
          <path d="M12.5 17.8C14.3 14.8 15 11.8 14.6 9C12.6 11.4 11.9 14.3 12.5 17.8Z" />

          {/* Middle left leaf */}
          <path d="M11.2 18C8.6 16 6.9 13.8 6.2 11.5C8.8 12.8 10.6 14.9 11.2 18Z" />

          {/* Middle right leaf */}
          <path d="M12.8 18C15.4 16 17.1 13.8 17.8 11.5C15.2 12.8 13.4 14.9 12.8 18Z" />

          {/* Broad outer left leaf */}
          <path d="M10.9 18.2C7.8 17.7 5.2 16.5 3.4 14.8C6.5 14.6 9 15.7 10.9 18.2Z" />

          {/* Broad outer right leaf */}
          <path d="M13.1 18.2C16.2 17.7 18.8 16.5 20.6 14.8C17.5 14.6 15 15.7 13.1 18.2Z" />

          {/* Low spreading left leaf */}
          <path d="M10.8 18.4C7.8 18.7 5.3 19.5 3.5 20.7C6.6 21.1 9 20.4 10.8 18.4Z" />

          {/* Low spreading right leaf */}
          <path d="M13.2 18.4C16.2 18.7 18.7 19.5 20.5 20.7C17.4 21.1 15 20.4 13.2 18.4Z" />

          {/* Compact basal crown */}
          <path d="M10.5 18.4C11.2 17.6 12.8 17.6 13.5 18.4" />
        </>
      );
      break;
    case "tufted":
      paths = (
        <>
          {/* Tall central shoots */}
          <path d="M11.3 21C11.1 16.2 11.3 10.9 12 4" />
          <path d="M12.7 21C13 16.3 13.8 11.4 15.2 6.3" />

          {/* Inner arching shoots */}
          <path d="M10.6 21C9.8 16.6 9.1 12.6 8.5 8.2" />
          <path d="M13.4 21C14.6 17.3 16 13.9 17.8 10.6" />

          {/* Outer spreading shoots */}
          <path d="M9.8 21C8.2 17.8 6.5 15.4 4.5 13.3" />
          <path d="M14.2 21C16.1 18.1 18 16.1 20.2 14.7" />

          {/* Shorter shoots filling the clump */}
          <path d="M10.9 21C10.3 18.2 9.9 15.7 10 13.2" />
          <path d="M13.1 21C13.8 18.5 14.5 16.4 15.5 14.5" />

          {/* Tight basal crown */}
          <path d="M8.8 21C10.4 20.1 13.6 20.1 15.2 21" />
        </>
      );
      break;
    case "stem":
      paths = (
        <>
          <path d="M12 21V3" />
          <path d="M12 9c-3 0-5-1.5-5-3.5 3 0 5 1.5 5 3.5ZM12 15c3 0 5-1.5 5-3.5-3 0-5 1.5-5 3.5Z" />
        </>
      );
      break;
    case "spines":
      paths = (
        <>
          {/* Bare stem: spines are modified leaves, so no normal leaves */}
          <path d="M12 21V3" />

          {/* Small paired spines */}
          <g strokeWidth="0.9">
            <path d="M12 7L9.8 5.9" />
            <path d="M12 7L14.2 5.9" />

            <path d="M12 11L9.6 10" />
            <path d="M12 11L14.4 10" />

            <path d="M12 15L9.8 14" />
            <path d="M12 15L14.2 14" />
          </g>
        </>
      );
      break;

    case "thorns":
      paths = (
        <>
          {/* Exact erect icon */}
          <path d="M12 21V3" />
          <path d="M12 9c-3 0-5-1.5-5-3.5 3 0 5 1.5 5 3.5ZM12 15c3 0 5-1.5 5-3.5-3 0-5 1.5-5 3.5Z" />

          {/* Two stout branch-like thorns */}
          <path d="M12 12.2L8.7 10.4" />
          <path d="M12 18.2L15.4 16.3" />
        </>
      );
      break;

    case "prickles":
      paths = (
        <>
          {/* Exact erect icon */}
          <path d="M12 21V3" />
          <path d="M12 9c-3 0-5-1.5-5-3.5 3 0 5 1.5 5 3.5ZM12 15c3 0 5-1.5 5-3.5-3 0-5 1.5-5 3.5Z" />

          {/* Small superficial prickles, alternating on both sides */}
          <g strokeWidth="1.05">
            {/* Left */}
            <path d="M12 11.3L10.2 10.5" />
            <path d="M12 17.2L10.3 16.4" />

            {/* Right */}
            <path d="M12 10.2L13.8 9.4" />
            <path d="M12 18.5L13.8 17.7" />
          </g>
        </>
      );
      break;
    case "alternate":
      paths = (
        <>
          <path d="M12 21V3" />
          <path d="M12 8c-4 0-6-1.5-6-4 4 0 6 1.5 6 4ZM12 15c4 0 6-1.5 6-4-4 0-6 1.5-6 4Z" />
        </>
      );
      break;
    case "opposite":
      paths = (
        <>
          <path d="M12 21V3" />
          <path d="M12 9C8 9 6 7.5 6 5c4 0 6 1.5 6 4ZM12 9c4 0 6-1.5 6-4-4 0-6 1.5-6 4ZM12 16c-3 0-5-1.3-5-3.5 3 0 5 1.3 5 3.5ZM12 16c3 0 5-1.3 5-3.5-3 0-5 1.3-5 3.5Z" />
        </>
      );
      break;
    case "whorled":
      paths = (
        <>
          {/* Main stem */}
          <path d="M12 21V3" />

          {/* Upper node: left + right + third leaf */}
          <path d="M12 9C8 9 6 7.5 6 5c4 0 6 1.5 6 4Z" />
          <path d="M12 9c4 0 6-1.5 6-4-4 0-6 1.5-6 4Z" />
          <path d="M12 9C10.8 6.2 11.3 4.1 13 2.8C14.4 5.5 13.8 7.6 12 9Z" />

          {/* Lower node: left + right + third leaf */}
          <path d="M12 16c-3 0-5-1.3-5-3.5 3 0 5 1.3 5 3.5Z" />
          <path d="M12 16c3 0 5-1.3 5-3.5-3 0-5 1.3-5 3.5Z" />
          <path d="M12 16C10.9 13.7 11.3 11.9 12.8 10.7C14 13 13.5 14.8 12 16Z" />
        </>
      );
      break;
    case "fascicled":
      paths = (
        <>
          {/* Main stem */}
          <path d="M12 21V3" />

          {/* Upper short spur */}
          <path d="M12 8L10.8 8" />

          {/* Upper fascicle: several leaves from one point */}
          <path d="M10.8 8C8.7 6.7 7.2 5.2 6.3 3.7" />
          <path d="M10.8 8C8.5 7.7 6.7 7 5.3 6" />
          <path d="M10.8 8C9.2 8.8 7.8 9.5 6.5 10.5" />

          {/* Lower short spur */}
          <path d="M12 15L13.2 15" />

          {/* Lower fascicle: several leaves from one point */}
          <path d="M13.2 15C15.2 13.6 16.7 12.2 17.7 10.7" />
          <path d="M13.2 15C15.5 14.7 17.3 14 18.7 13" />
          <path d="M13.2 15C14.9 15.8 16.3 16.7 17.5 17.8" />
        </>
      );
      break;
    case "basal":
    case "rosette":
      paths = (
        <>
          {/* Central upright leaf */}
          <path d="M12 16C10.9 12.6 11.1 9.3 12.4 6.1C13.8 9.4 13.5 12.7 12 16Z" />

          {/* Upper-left leaf */}
          <path d="M11.5 16C9.3 13.5 8.1 10.9 8.4 8.3C10.7 10.1 11.9 12.7 11.5 16Z" />

          {/* Upper-right leaf */}
          <path d="M12.5 16C14.7 13.4 15.9 10.8 15.6 8.2C13.3 10 12.1 12.7 12.5 16Z" />

          {/* Broad left leaf */}
          <path d="M11.3 16.2C8.2 15.1 5.9 13.5 4.5 11.4C7.6 11.6 10 13.2 11.3 16.2Z" />

          {/* Broad right leaf */}
          <path d="M12.7 16.2C15.8 15.1 18.1 13.5 19.5 11.4C16.4 11.6 14 13.2 12.7 16.2Z" />

          {/* Lower-left spreading leaf */}
          <path d="M11.2 16.5C8.2 16.6 5.7 17.5 3.8 19C6.9 19.6 9.5 18.8 11.2 16.5Z" />

          {/* Lower-right spreading leaf */}
          <path d="M12.8 16.5C15.8 16.6 18.3 17.5 20.2 19C17.1 19.6 14.5 18.8 12.8 16.5Z" />

          {/* Small central overlap */}
          <path d="M10.8 16.4C11.5 15.7 12.5 15.7 13.2 16.4C12.5 17.1 11.5 17.1 10.8 16.4Z" />
        </>
      );
      break;
    case "compound":
      paths = (
        <>
          {/* Generic compound leaf */}
          <path d="M12 21V4" />
          <path d="M12 9C9.2 9 7.5 7.8 7 6.2C9.6 6.1 11.3 7.1 12 9Z" />
          <path d="M12 9C14.8 9 16.5 7.8 17 6.2C14.4 6.1 12.7 7.1 12 9Z" />
          <path d="M12 15C9.2 15 7.5 13.8 7 12.2C9.6 12.1 11.3 13.1 12 15Z" />
          <path d="M12 15C14.8 15 16.5 13.8 17 12.2C14.4 12.1 12.7 13.1 12 15Z" />
          <path d="M12 4C10.7 2.8 10.9 1.5 12 1C13.1 1.5 13.3 2.8 12 4Z" />
        </>
      );
      break;

    case "compoundUnifoliolate":
      paths = (
        <>
          {/* Petiole */}
          <path d="M12 21V13" />

          {/* Joint distinguishing leaflet from a simple blade */}
          <circle cx="12" cy="12" r="0.8" />

          {/* Single leaflet */}
          <path d="M12 11C8.8 9.1 8.5 5.3 12 2C15.5 5.3 15.2 9.1 12 11Z" />
        </>
      );
      break;

    case "compoundBifoliate":
      paths = (
        <>
          {/* Petiole */}
          <path d="M12 21V12" />

          {/* Two leaflet stalks */}
          <path d="M12 12L8.5 8.5" />
          <path d="M12 12L15.5 8.5" />

          {/* Two leaflets */}
          <path d="M8.5 8.5C5.7 8.3 4.2 6.4 4.7 4.2C7.3 4.5 8.8 6.1 8.5 8.5Z" />
          <path d="M15.5 8.5C18.3 8.3 19.8 6.4 19.3 4.2C16.7 4.5 15.2 6.1 15.5 8.5Z" />
        </>
      );
      break;

    case "compoundTrifoliate":
      paths = (
        <>
          {/* Petiole */}
          <path d="M12 21V12" />

          {/* Three leaflet stalks */}
          <path d="M12 12L7.5 8.5" />
          <path d="M12 12L16.5 8.5" />
          <path d="M12 12V7" />

          {/* Left leaflet */}
          <path d="M7.5 8.5C4.8 8.2 3.4 6.4 3.9 4.3C6.4 4.5 7.8 6.1 7.5 8.5Z" />

          {/* Right leaflet */}
          <path d="M16.5 8.5C19.2 8.2 20.6 6.4 20.1 4.3C17.6 4.5 16.2 6.1 16.5 8.5Z" />

          {/* Terminal leaflet */}
          <path d="M12 7C10.2 5.1 10.5 2.9 12 1.5C13.5 2.9 13.8 5.1 12 7Z" />
        </>
      );
      break;

    case "compoundPinnate":
      paths = (
        <>
          {/* Rachis */}
          <path d="M12 21V4" />

          {/* Three paired leaflet levels */}
          <path d="M12 9C9.5 9 8 8 7.4 6.5C9.8 6.4 11.4 7.3 12 9Z" />
          <path d="M12 9C14.5 9 16 8 16.6 6.5C14.2 6.4 12.6 7.3 12 9Z" />

          <path d="M12 13.5C9.3 13.5 7.7 12.4 7.1 10.9C9.7 10.8 11.3 11.7 12 13.5Z" />
          <path d="M12 13.5C14.7 13.5 16.3 12.4 16.9 10.9C14.3 10.8 12.7 11.7 12 13.5Z" />

          <path d="M12 18C9.5 18 8 17 7.4 15.5C9.8 15.4 11.4 16.3 12 18Z" />
          <path d="M12 18C14.5 18 16 17 16.6 15.5C14.2 15.4 12.6 16.3 12 18Z" />
        </>
      );
      break;

    case "compoundImparipinnate":
      paths = (
        <>
          {/* Rachis */}
          <path d="M12 21V5" />

          {/* Paired lateral leaflets */}
          <path d="M12 10.5C9.4 10.5 7.8 9.4 7.2 7.9C9.7 7.8 11.3 8.7 12 10.5Z" />
          <path d="M12 10.5C14.6 10.5 16.2 9.4 16.8 7.9C14.3 7.8 12.7 8.7 12 10.5Z" />

          <path d="M12 16C9.4 16 7.8 14.9 7.2 13.4C9.7 13.3 11.3 14.2 12 16Z" />
          <path d="M12 16C14.6 16 16.2 14.9 16.8 13.4C14.3 13.3 12.7 14.2 12 16Z" />

          {/* Single terminal leaflet */}
          <path d="M12 5C10.4 3.5 10.6 1.8 12 1C13.4 1.8 13.6 3.5 12 5Z" />
        </>
      );
      break;

    case "compoundParipinnate":
      paths = (
        <>
          {/* Rachis */}
          <path d="M12 21V5.5" />

          {/* Lower pairs */}
          <path d="M12 15.5C9.4 15.5 7.8 14.4 7.2 12.9C9.7 12.8 11.3 13.7 12 15.5Z" />
          <path d="M12 15.5C14.6 15.5 16.2 14.4 16.8 12.9C14.3 12.8 12.7 13.7 12 15.5Z" />

          <path d="M12 10.5C9.4 10.5 7.8 9.4 7.2 7.9C9.7 7.8 11.3 8.7 12 10.5Z" />
          <path d="M12 10.5C14.6 10.5 16.2 9.4 16.8 7.9C14.3 7.8 12.7 8.7 12 10.5Z" />

          {/* Terminal pair, no single terminal leaflet */}
          <path d="M12 5.5C9.7 5.4 8.3 4.4 7.8 2.9C10 2.8 11.4 3.7 12 5.5Z" />
          <path d="M12 5.5C14.3 5.4 15.7 4.4 16.2 2.9C14 2.8 12.6 3.7 12 5.5Z" />
        </>
      );
      break;

    case "compoundPalmate":
      paths = (
        <>
          {/* Petiole */}
          <path d="M12 21V13" />

          {/* Five leaflet stalks radiating from one point */}
          <path d="M12 13L5.8 9" />
          <path d="M12 13L8.8 6.5" />
          <path d="M12 13V5" />
          <path d="M12 13L15.2 6.5" />
          <path d="M12 13L18.2 9" />

          {/* Leaflets */}
          <path d="M5.8 9C3.7 8.6 2.7 7.1 3.2 5.5C5.2 5.7 6.3 7 5.8 9Z" />
          <path d="M8.8 6.5C7 5.4 6.6 3.6 7.5 2.4C9.2 3.4 9.7 5 8.8 6.5Z" />
          <path d="M12 5C10.7 3.4 10.9 1.8 12 1C13.1 1.8 13.3 3.4 12 5Z" />
          <path d="M15.2 6.5C17 5.4 17.4 3.6 16.5 2.4C14.8 3.4 14.3 5 15.2 6.5Z" />
          <path d="M18.2 9C20.3 8.6 21.3 7.1 20.8 5.5C18.8 5.7 17.7 7 18.2 9Z" />
        </>
      );
      break;

    case "compoundBipinnate":
      paths = (
        <>
          {/* Main rachis */}
          <path d="M12 21V4" />

          {/* Primary pinnae */}
          <path d="M12 9L6.5 6.5" />
          <path d="M12 9L17.5 6.5" />
          <path d="M12 15L5.5 12.5" />
          <path d="M12 15L18.5 12.5" />

          {/* Secondary leaflet divisions */}
          <g strokeWidth="1">
            <path d="M7.8 7.1L6.7 5.3M9.2 7.7L8.1 9.1" />
            <path d="M16.2 7.1L17.3 5.3M14.8 7.7L15.9 9.1" />

            <path d="M7.2 13.2L6 11.2M8.8 14L7.7 15.7" />
            <path d="M16.8 13.2L18 11.2M15.2 14L16.3 15.7" />
          </g>
        </>
      );
      break;

    case "compoundTripinnate":
      paths = (
        <>
          {/* Main rachis */}
          <path d="M12 21V4" />

          {/* Primary divisions */}
          <path d="M12 10L7 7" />
          <path d="M12 10L17 7" />
          <path d="M12 16L6.5 13" />
          <path d="M12 16L17.5 13" />

          {/* Secondary divisions */}
          <g strokeWidth="1">
            <path d="M8.6 8L6.7 5.8M8.6 8L5.9 8.6" />
            <path d="M15.4 8L17.3 5.8M15.4 8L18.1 8.6" />

            <path d="M8.3 14L6.2 11.8M8.3 14L5.4 14.6" />
            <path d="M15.7 14L17.8 11.8M15.7 14L18.6 14.6" />
          </g>

          {/* Third division level */}
          <g strokeWidth="0.7">
            <path d="M6.7 5.8L5.6 4.8M6.7 5.8L7 4.4" />
            <path d="M17.3 5.8L18.4 4.8M17.3 5.8L17 4.4" />
            <path d="M6.2 11.8L5.1 10.8M6.2 11.8L6.5 10.4" />
            <path d="M17.8 11.8L18.9 10.8M17.8 11.8L17.5 10.4" />
          </g>
        </>
      );
      break;
    case "marginEntire":
      paths = (
        <>
          {/* Original smooth-edged leaf */}
          <path d="M20 4C12 4 6 7 4.5 12.5C3.5 16 6 19 9.5 19C15 19 18.5 13 20 4Z" />

          {/* Midvein / petiole */}
          <path d="M5 20C8 15 12 11 17 8" />
        </>
      );
      break;

    case "marginSerrate":
      paths = (
        <>
          <g strokeWidth="0.95">
            {/* Broad Entire-like leaf with only the margin made serrate */}
            <path
              d="
            M20 4

            L18.1 4.5
            L18.5 5.2
            L16.4 5.5
            L16.8 6.3
            L14.6 6.7
            L15 7.5
            L12.8 8.1
            L13.2 8.9
            L11 9.7
            L11.4 10.5
            L9.2 11.3
            L9.6 12.1
            L7.4 12.8
            L7.8 13.6
            L5.7 14.2
            L6.1 15
            L4.7 15.5

            C4.6 17.2 6.5 18.8 9.5 19

            L10.4 17.3
            L11.2 17.9
            L12.1 16
            L13 16.6
            L13.9 14.5
            L14.9 15
            L15.7 12.6
            L16.7 13.1
            L17.3 10.6
            L18.3 11
            L18.7 8.4
            L19.5 8.8
            L19.4 6.2
            L20.2 6.6

            Z
          "
            />

            {/* Long petiole continuing as the midvein all the way to the apex */}
            <path d="M3.6 21.5C7.2 16.6 11.8 10.7 20 4" />
          </g>
        </>
      );
      break;

    case "marginDentate":
      paths = (
        <>
          <g strokeWidth="1.15">
            {/* Larger triangular teeth projecting outward */}
            <path
              d="
          M20 4
          L16.9 4.8
          L16 6.4
          L13.5 6.2
          L12.5 8.4
          L10 8.5
          L9.1 11
          L6.8 11.6
          L6.4 14.2
          L4.3 15.5
          C4.6 17.5 6.8 19 9.5 19

          L10.8 16.7
          L13.3 17
          L13.7 14.4
          L16 14
          L15.7 11.3
          L18 10.3
          L17.4 7.7
          L19.5 6.3
          Z
        "
            />

            <path d="M5 20C8 15 12 11 17 8" />
          </g>
        </>
      );
      break;

    case "marginCrenate":
      paths = (
        <>
          <g strokeWidth="1.05">
            {/* Rounded scallops rather than pointed teeth */}
            <path
              d="
          M20 4

          C18.3 4.1 17.4 4.8 16.7 5.6
          C15.7 5.2 14.6 5.6 14 6.6
          C12.8 6.3 11.7 6.9 11.2 8
          C10 7.9 8.9 8.7 8.6 9.9
          C7.3 10 6.3 11 6.2 12.2
          C4.9 12.6 4.2 13.8 4.5 15.1
          C4.6 17.4 6.6 19 9.5 19

          C10.6 19.1 11.6 18.5 12 17.5
          C13.3 17.8 14.3 16.9 14.5 15.7
          C15.8 15.6 16.6 14.5 16.5 13.3
          C17.7 13 18.3 11.8 18 10.7
          C19 10.1 19.3 8.8 18.8 7.8
          C19.7 6.8 20 5.4 20 4
          Z
        "
            />

            <path d="M5 20C8 15 12 11 17 8" />
          </g>
        </>
      );
      break;

    case "marginLobed":
      paths = (
        <>
          <g strokeWidth="1.15">
            {/* Dramatically lobed Quercus-like leaf */}
            <path
              d="
            M12 2.5
            C13.1 3.1 13.8 4 14.1 5
            C15.3 4.1 16.8 4.2 17.6 5.3
            C18.4 6.4 17.6 7.7 15.7 8.1
            C17.3 8.3 18.4 9.4 18.1 10.8
            C17.8 12.2 16.1 12.8 14.4 12.2
            C15.5 13.4 15.7 14.8 14.7 15.8
            C14 16.6 13 16.7 12.4 16.1
            C12.5 17 12.3 17.8 12 18.5
            C11.7 17.8 11.5 17 11.6 16.1
            C11 16.7 10 16.6 9.3 15.8
            C8.3 14.8 8.5 13.4 9.6 12.2
            C7.9 12.8 6.2 12.2 5.9 10.8
            C5.6 9.4 6.7 8.3 8.3 8.1
            C6.4 7.7 5.6 6.4 6.4 5.3
            C7.2 4.2 8.7 4.1 9.9 5
            C10.2 4 10.9 3.1 12 2.5
            Z
          "
            />

            {/* Central midrib and petiole */}
            <path d="M12 2.5V21.5" />
          </g>
        </>
      );
      break;

    case "marginUndulate":
      paths = (
        <>
          {/* More visible smooth waves with a finer stroke */}
          <g strokeWidth="1.15">
            <path
              d="
            M20 4

            C18.2 3.8 17.2 5.4 15.8 5
            C14.5 4.7 14 6.5 12.6 6.2
            C11.2 5.9 10.8 7.8 9.4 7.8
            C8 7.8 8 9.8 6.7 10.2
            C5.4 10.6 5.8 12.5 4.7 13.3
            C3.7 14.2 4.4 16.1 5.8 17.4
            C6.8 18.4 8.1 19 9.5 19

            C10.8 19 11.3 17.2 12.7 17.5
            C14.1 17.8 14.1 15.9 15.4 15.7
            C16.7 15.5 16.2 13.6 17.4 13
            C18.6 12.4 17.8 10.6 18.8 9.7
            C19.8 8.8 18.8 7.2 19.5 6
            C19.9 5.3 20 4.6 20 4
            Z
          "
            />

            <path d="M5 20C8 15 12 11 17 8" />
          </g>
        </>
      );
      break;

    case "marginSpinose":
      paths = (
        <>
          <g strokeWidth="1.15">
            {/* Same leaf with fewer, much longer rigid spine-like points */}
            <path
              d="
          M20 4

          L16.7 5
          L16.8 2.8
          L14.4 6.4

          L11.8 7.5
          L11 5.3
          L9.4 9.5

          L6.9 11.6
          L4.7 10.2
          L5.7 14.2

          L3.1 16.2
          L6.3 16.5
          C7 18.1 8.1 19 9.5 19

          L11 16
          L12.9 18
          L13.5 14.4

          L16.2 13
          L18.1 14
          L17 10.6

          L19 8
          L21.2 8.3
          L19.1 6
          Z
        "
            />

            <path d="M5 20C8 15 12 11 17 8" />
          </g>
        </>
      );
      break;

    case "shapeElliptic":
      paths = (
        <>
          {/* Widest at the middle, tapering similarly toward both ends */}
          <path d="M12 3C16.2 4.8 18.3 8.1 18 11.8C17.7 15.1 15.4 17.7 12 19C8.6 17.7 6.3 15.1 6 11.8C5.7 8.1 7.8 4.8 12 3Z" />
          <path d="M12 19V22" />
        </>
      );
      break;

    case "shapeOvate":
      paths = (
        <>
          {/* Egg-shaped: widest below the middle */}
          <path d="M12 3C14.7 5 17 8.4 18 12.7C18.7 15.7 16.2 18.1 12 19C7.8 18.1 5.3 15.7 6 12.7C7 8.4 9.3 5 12 3Z" />
          <path d="M12 19V22" />
        </>
      );
      break;

    case "shapeObovate":
      paths = (
        <>
          {/* Reverse egg-shaped: widest above the middle */}
          <path d="M12 3C16.1 3.6 18.7 6.1 18.2 9.6C17.7 13.6 15.3 16.8 12 19C8.7 16.8 6.3 13.6 5.8 9.6C5.3 6.1 7.9 3.6 12 3Z" />
          <path d="M12 19V22" />
        </>
      );
      break;

    case "shapeLanceolate":
      paths = (
        <>
          {/* Long and narrow, widest below the middle */}
          <path d="M12 2.5C14.1 5 15.7 9 16.2 13.1C16.5 16.1 14.5 18.8 12 20C9.5 18.8 7.5 16.1 7.8 13.1C8.3 9 9.9 5 12 2.5Z" />
          <path d="M12 20V22" />
        </>
      );
      break;

    case "shapeOblanceolate":
      paths = (
        <>
          {/* Long and narrow, widest above the middle */}
          <path d="M12 2.5C15.3 4.4 17 7 16.7 10.1C16.2 14 14.1 17.3 12 20C9.9 17.3 7.8 14 7.3 10.1C7 7 8.7 4.4 12 2.5Z" />
          <path d="M12 20V22" />
        </>
      );
      break;

    case "shapeLinear":
      paths = (
        <>
          {/* Very narrow with nearly parallel sides */}
          <path d="M12 2.5C13 3.7 13.5 5.1 13.5 7V17.5C13.5 18.7 12.8 19.6 12 20.2C11.2 19.6 10.5 18.7 10.5 17.5V7C10.5 5.1 11 3.7 12 2.5Z" />
          <path d="M12 20.2V22" />
        </>
      );
      break;

    case "shapeCordate":
      paths = (
        <>
          {/* Heart-shaped blade with basal notch */}
          <path d="M12 3C16.5 6 18.5 10 18 13.5C17.7 16.2 15.6 18 13.3 18C12.7 18 12.3 17.2 12 16C11.7 17.2 11.3 18 10.7 18C8.4 18 6.3 16.2 6 13.5C5.5 10 7.5 6 12 3Z" />

          {/* Petiole arising from basal sinus */}
          <path d="M12 16V22" />
        </>
      );
      break;

    case "shapeOrbicular":
      paths = (
        <>
          {/* Approximately circular blade */}
          <path d="M12 4C16.4 4 19 7.3 19 11.5C19 15.6 16 18.5 12 18.5C8 18.5 5 15.6 5 11.5C5 7.3 7.6 4 12 4Z" />
          <path d="M12 18.5V22" />
        </>
      );
      break;

    case "shapeSagittate":
      paths = (
        <>
          {/* Arrowhead-shaped blade with backward-pointing basal lobes */}
          <path d="M12 2.5C14.5 5 17 7.5 19 10.5C16.8 10.2 15.2 10.8 14.2 12.3L17.2 19C14.9 18 13.2 16.7 12 15.2C10.8 16.7 9.1 18 6.8 19L9.8 12.3C8.8 10.8 7.2 10.2 5 10.5C7 7.5 9.5 5 12 2.5Z" />

          {/* Petiole from basal sinus */}
          <path d="M12 15.2V22" />
        </>
      );
      break;
    case "shapeOblong":
      paths = (
        <>
          {/* Long blade with nearly parallel sides and rounded ends */}
          <path d="M12 3C15.2 3 16.6 5.6 16.6 9V13.8C16.6 17.1 14.8 19 12 19C9.2 19 7.4 17.1 7.4 13.8V9C7.4 5.6 8.8 3 12 3Z" />
          <path d="M12 19V22" />
        </>
      );
      break;

    case "shapeAcicular":
      paths = (
        <>
          {/* Needle-shaped blade */}
          <path d="M12 2.5C13.1 6.5 13.2 12.7 12.6 19.5L12 21L11.4 19.5C10.8 12.7 10.9 6.5 12 2.5Z" />
          <path d="M12 21V22" />
        </>
      );
      break;

    case "shapeSpatulate":
      paths = (
        <>
          {/* Broad rounded apex narrowing strongly toward base */}
          <path d="M12 3C15.8 3 18 5.6 17.4 8.7C16.9 11.5 14.6 13.2 13.2 15.2C12.5 16.2 12.2 17.5 12 19C11.8 17.5 11.5 16.2 10.8 15.2C9.4 13.2 7.1 11.5 6.6 8.7C6 5.6 8.2 3 12 3Z" />
          <path d="M12 19V22" />
        </>
      );
      break;

    case "shapeReniform":
      paths = (
        <>
          {/* Kidney-shaped blade with basal sinus */}
          <path d="M12 16.5C10.5 18.1 8.1 18.3 6.2 17.1C3.8 15.6 3.2 12.3 4.2 9.4C5.4 6 8.2 4.5 12 5.8C15.8 4.5 18.6 6 19.8 9.4C20.8 12.3 20.2 15.6 17.8 17.1C15.9 18.3 13.5 18.1 12 16.5Z" />

          {/* Basal notch / petiole */}
          <path d="M12 16.5V22" />
        </>
      );
      break;

    case "shapeDeltoid":
      paths = (
        <>
          {/* Broad triangular blade */}
          <path d="M12 3L19 17C16.4 18.3 14.1 18.8 12 18.8C9.9 18.8 7.6 18.3 5 17L12 3Z" />
          <path d="M12 18.8V22" />
        </>
      );
      break;

    case "shapeRhombic":
      paths = (
        <>
          {/* Diamond-shaped blade */}
          <path d="M12 3L18.2 11.3L12 19L5.8 11.3L12 3Z" />
          <path d="M12 19V22" />
        </>
      );
      break;

    case "shapeHastate":
      paths = (
        <>
          {/* Spear-shaped blade */}
          <path d="M12 2.5C14.1 5.2 15.8 8 16.6 10.7L21 12.4C18.7 13.4 16.7 14 14.5 14L12 18L9.5 14C7.3 14 5.3 13.4 3 12.4L7.4 10.7C8.2 8 9.9 5.2 12 2.5Z" />

          {/* Petiole */}
          <path d="M12 18V22" />
        </>
      );
      break;
    case "detail":
      paths = (
        <>
          <path d="M19.5 4.5C12 4.5 6.5 7 5 12c-1 3.5 1.5 6.5 5 6.5 5.5 0 8.5-6 9.5-14Z" />
          <circle cx="12" cy="11" r="1" />
          <circle cx="15.5" cy="8" r="1" />
        </>
      );
      break;
    case "drop":
      paths = <path d="M12 3s6 7 6 12a6 6 0 0 1-12 0c0-5 6-12 6-12Z" />;
      break;
    case "flower":
      paths = (
        <>
          <circle cx="12" cy="12" r="2" />
          <path d="M12 10c-2-5 3-7 4-3 .5 2-1 4-4 5M14 12c5-2 7 3 3 4-2 .5-4-1-5-4M12 14c2 5-3 7-4 3-.5-2 1-4 4-5M10 12c-5 2-7-3-3-4 2-.5 4 1 5 4" />
        </>
      );
      break;
    case "inflorescence":
      paths = (
        <>
          <path d="M12 21V7M12 12l-5-4M12 15l5-5" />
          <circle cx="12" cy="5" r="2" />
          <circle cx="6" cy="7" r="2" />
          <circle cx="18" cy="9" r="2" />
        </>
      );
      break;
    case "pod":
      paths = (
        <>
          <path d="M5 18c8 1 13-4 14-12-8-1-13 4-14 12Z" />
          <circle cx="9" cy="15" r=".8" />
          <circle cx="12" cy="12" r=".8" />
          <circle cx="15" cy="9" r=".8" />
        </>
      );
      break;
    case "wing":
      paths = (
        <>
          <circle cx="7" cy="17" r="2" />
          <path d="M9 16c7-1 10-5 10-11-6 0-10 4-10 11Z" />
        </>
      );
      break;
    case "cone":
      paths = (
        <>
          <path d="M12 3c-5 5-6 11-3 17h6c3-6 2-12-3-17Z" />
          <path d="m9 9 6 4M15 9l-6 4M9 15l6 3" />
        </>
      );
      break;
    case "fruit":
      paths = (
        <>
          <path d="M12 8c-5-2-8 2-7 7 1 5 5 7 7 5 2 2 6 0 7-5 1-5-2-9-7-7Z" />
          <path d="M12 8c0-3 1-5 4-6M13 5c3 0 5 1 6 3-3 1-5 0-6-3Z" />
        </>
      );
      break;
    case "scent":
      paths = (
        <>
          <path d="M7 20c-2-2-2-4 0-6s2-4 0-6M12 20c-2-2-2-4 0-6s2-4 0-6M17 20c-2-2-2-4 0-6s2-4 0-6" />
        </>
      );
      break;
    case "root":
      paths = (
        <>
          <path d="M12 3v8M12 11c-4 2-5 5-5 10M12 11c4 2 5 5 5 10M12 14c-1 3-1 5 0 7M8 16l-3 2M16 16l3 2" />
        </>
      );
      break;
    default:
      paths = (
        <path d="M20 4C12 4 6 7 4.5 12.5 3.5 16 6 19 9.5 19 15 19 18.5 13 20 4ZM5 20c3-5 7-9 12-12" />
      );
  }

  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.7"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      className={className}
    >
      {paths}
    </svg>
  );
}

function TraitVisual({ field, value, className = "h-4 w-4" }) {
  if (field?.icon === false) return null;

  if (field?.key === "exudate") {
    const fill = EXUDATE_SWATCHES[value] || EXUDATE_SWATCHES.clear;

    return (
      <svg
        viewBox="0 0 24 24"
        aria-hidden="true"
        className={`${className} shrink-0`}
      >
        <path
          d="M12 2.8s6.2 7.2 6.2 12.1a6.2 6.2 0 0 1-12.4 0C5.8 10 12 2.8 12 2.8Z"
          fill={fill}
          stroke="#374049"
          strokeWidth="1.35"
          strokeLinejoin="round"
        />
        {value === "clear" && (
          <path
            d="M9 14.6c.15 1.7 1.05 2.7 2.55 3.05"
            fill="none"
            stroke="#ffffff"
            strokeWidth="1.4"
            strokeLinecap="round"
            opacity="0.9"
          />
        )}
        {value === "absent" && (
          <path
            d="m7.4 7.4 9.2 9.2"
            fill="none"
            stroke="#d94a4a"
            strokeWidth="1.8"
            strokeLinecap="round"
          />
        )}
      </svg>
    );
  }

  if (
    [
      "upperLeafColor",
      "lowerLeafColor",
      "flowerColor",
      "fruitColor",
      "seedColor",
    ].includes(field?.key)
  ) {
    return (
      <span
        aria-hidden="true"
        className={`${className} inline-block shrink-0 rounded-full border border-[#374049]/25 shadow-inner`}
        style={{ backgroundColor: COLOR_SWATCHES[value] || "#ffffff" }}
      />
    );
  }

  return (
    <TraitGlyph fieldKey={field.key} value={value} className={className} />
  );
}

function TraitField({
  field,
  value,
  onChange,
  inheritedSource = "",
  copyFromValue = "",
  onCopyFrom,
}) {
  const maxSelections = Number(field.maxSelections || 1);
  const multiSelect = maxSelections > 1;
  const locked = Boolean(inheritedSource);

  const selectedValues = (
    multiSelect ? (Array.isArray(value) ? value : value ? [value] : []) : []
  ).filter((selectedValue) =>
    field.options.some(([optionValue]) => optionValue === selectedValue),
  );

  const hasValue = multiSelect ? selectedValues.length > 0 : !!value;

  function handleOptionClick(optionValue) {
    if (locked) return;

    if (!multiSelect) {
      onChange(value === optionValue ? "" : optionValue);
      return;
    }

    if (selectedValues.includes(optionValue)) {
      onChange(selectedValues.filter((item) => item !== optionValue));
      return;
    }

    if (FLORAL_PART_COUNT_KEYS.has(field.key) && optionValue === "many") {
      onChange(["many"]);
      return;
    }

    const currentSelections = FLORAL_PART_COUNT_KEYS.has(field.key)
      ? selectedValues.filter((item) => item !== "many")
      : selectedValues;

    if (currentSelections.length >= maxSelections) return;

    onChange([...currentSelections, optionValue]);
  }

  return (
    <fieldset
      className={`rounded-2xl border p-4 shadow-sm sm:p-5 ${
        locked
          ? "border-[#24C3BE]/45 bg-[#FCFFF6]"
          : "border-[#93CA87]/35 bg-white"
      }`}
    >
      <legend className="sr-only">{field.label}</legend>

      <div className="flex flex-col gap-1 sm:flex-row sm:items-start sm:justify-between sm:gap-4">
        <div>
          <div className="flex flex-wrap items-center gap-2">
            <h4 className="text-sm font-semibold text-[#374049]">
              {field.label}
            </h4>

            {locked && (
              <span className="rounded-full border border-[#24C3BE]/45 bg-white px-2 py-0.5 text-[9px] font-bold uppercase tracking-wide text-[#01767A]">
                Inherited
              </span>
            )}
          </div>

          <p className="mt-0.5 text-xs leading-relaxed text-[#374049]/55">
            {field.hint}
          </p>
        </div>

        {!locked && (
          <div className="mt-1 flex shrink-0 items-center gap-3 self-start sm:mt-0">
            {field.copyFrom && copyFromValue && onCopyFrom && (
              <button
                type="button"
                onClick={() => onCopyFrom(copyFromValue)}
                className="text-[10px] font-semibold uppercase tracking-wide text-[#01767A] transition hover:text-[#24C3BE]"
              >
                {field.copyLabel || "Copy"}
              </button>
            )}

            {hasValue && (
              <button
                type="button"
                onClick={() => onChange(multiSelect ? [] : "")}
                className="text-[10px] font-semibold uppercase tracking-wide text-[#374049]/45 transition hover:text-red-600"
              >
                Clear
              </button>
            )}
          </div>
        )}
      </div>

      <div className="mt-3 flex flex-wrap gap-2">
        {field.options.map(([optionValue, optionLabel]) => {
          const selected = multiSelect
            ? selectedValues.includes(optionValue)
            : value === optionValue;

          const selectionIndex = multiSelect
            ? selectedValues.indexOf(optionValue)
            : -1;

          const limitReached =
            multiSelect &&
            !selected &&
            selectedValues.length >= maxSelections &&
            !(FLORAL_PART_COUNT_KEYS.has(field.key) && optionValue === "many");

          const optionHelp = getOptionHelp(field, optionValue, optionLabel);

          return (
            <button
              key={optionValue}
              type="button"
              disabled={limitReached}
              aria-disabled={locked || limitReached}
              aria-pressed={selected}
              aria-label={`${optionLabel}. ${optionHelp}`}
              title={
                locked
                  ? `Inherited from ${inheritedSource}; this value cannot be changed for this species. ${optionHelp}`
                  : limitReached
                    ? `A maximum of ${maxSelections} options can be selected.`
                    : optionHelp
              }
              onClick={() => handleOptionClick(optionValue)}
              className={`group relative rounded-xl border px-3 py-2 text-xs font-medium transition sm:text-[13px] ${
                selected
                  ? locked
                    ? "cursor-not-allowed border-[#01767A]/45 bg-[#24C3BE]/15 text-[#01767A] shadow-sm"
                    : "border-[#01767A] bg-[#24C3BE] text-white shadow-sm"
                  : locked || limitReached
                    ? "cursor-not-allowed border-[#93CA87]/25 bg-slate-50 text-[#374049]/30"
                    : "border-[#93CA87]/45 bg-white text-[#374049] hover:border-[#24C3BE] hover:bg-[#24C3BE]/5"
              }`}
            >
              <span className="inline-flex items-center justify-center gap-1.5">
                {field.icon !== false && (
                  <span
                    className={`shrink-0 ${
                      selected && !locked ? "text-white" : "text-[#01767A]"
                    }`}
                  >
                    <TraitVisual
                      field={field}
                      value={optionValue}
                      className="h-4 w-4"
                    />
                  </span>
                )}

                <span>{optionLabel}</span>

                {selected && (
                  <span
                    aria-hidden="true"
                    className={`ml-0.5 text-[10px] font-bold ${
                      locked ? "text-[#01767A]" : "text-white"
                    }`}
                  >
                    {locked ? "◆" : multiSelect ? selectionIndex + 1 : "✓"}
                  </span>
                )}
              </span>

              {(!limitReached || locked) && (
                <span className="pointer-events-none absolute bottom-full left-0 z-50 mb-2 hidden w-56 max-w-[calc(100vw-3rem)] rounded-xl bg-[#374049] px-3 py-2 text-left text-[10px] font-normal leading-relaxed text-white shadow-xl group-hover:block group-focus-visible:block sm:left-1/2 sm:-translate-x-1/2">
                  {optionHelp}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {multiSelect && selectedValues.length > 0 && !locked && (
        <div className="mt-3 flex flex-wrap items-center gap-2 border-t border-[#93CA87]/25 pt-3">
          <span className="inline-flex items-center gap-2 rounded-xl border border-[#24C3BE]/45 bg-[#FCFFF6] px-3 py-2 text-xs font-semibold text-[#374049] shadow-sm">
            {field.icon !== false && (
              <span className="flex shrink-0 items-center gap-0.5 text-[#01767A]">
                {selectedValues.map((selectedValue) => (
                  <TraitVisual
                    key={selectedValue}
                    field={field}
                    value={selectedValue}
                    className="h-4 w-4"
                  />
                ))}
              </span>
            )}

            <span>{traitLabel(field.key, selectedValues)}</span>
          </span>

          <span className="text-[10px] text-[#374049]/45">
            {selectedValues.length}/{maxSelections} selected
          </span>
        </div>
      )}
    </fieldset>
  );
}

export default function PlantSpeciesDescription({
  scientificName,
  enabled = false,
  buttonOnly = false,
  detailButton = false,
  className = "",
}) {
  const descriptionId = useMemo(
    () => getDescriptionId(scientificName),
    [scientificName],
  );
  const traitInheritance = useMemo(
    () => getTaxonomyTraitInheritance(scientificName),
    [scientificName],
  );
  const reproductiveGroup = useMemo(
    () => getPlantReproductiveGroup(scientificName),
    [scientificName],
  );
  const visibleDescriptionSections = useMemo(
    () =>
      DESCRIPTION_SECTIONS.filter((section) =>
        descriptionSectionIsVisible(section, reproductiveGroup),
      ),
    [reproductiveGroup],
  );

  const [description, setDescription] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState("");
  const [editorOpen, setEditorOpen] = useState(false);
  const [activeSection, setActiveSection] = useState("habit");
  const [draftTraits, setDraftTraits] = useState({});
  const [baseRevision, setBaseRevision] = useState(0);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState("");
  const editorContentRef = useRef(null);

  useEffect(() => {
    if (!enabled || !descriptionId) {
      setDescription(null);
      setLoading(false);
      setLoadError("");
      return undefined;
    }

    setLoading(true);
    setLoadError("");

    const descriptionRef = doc(db, "speciesDescriptions", descriptionId);

    return onSnapshot(
      descriptionRef,
      (snapshot) => {
        setDescription(
          snapshot.exists() ? { id: snapshot.id, ...snapshot.data() } : null,
        );
        setLoading(false);
      },
      (error) => {
        console.error("Failed to load plant description:", error);
        setLoadError("The shared plant description could not be loaded.");
        setLoading(false);
      },
    );
  }, [descriptionId, enabled]);

  useEffect(() => {
    if (!editorOpen) return undefined;

    function handleKeyDown(event) {
      if (event.key === "Escape" && !saving) setEditorOpen(false);
    }

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", handleKeyDown);

    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [editorOpen, saving]);

  useEffect(() => {
    if (!editorOpen) return undefined;

    const animationFrame = window.requestAnimationFrame(() => {
      editorContentRef.current?.scrollTo({
        top: 0,
        left: 0,
        behavior: "auto",
      });
    });

    return () => window.cancelAnimationFrame(animationFrame);
  }, [activeSection, editorOpen]);

  if (!enabled || !scientificName) return null;

  const inheritedTraits = traitInheritance?.traits || EMPTY_TRAITS;
  const manualRecordedTraits = removeInheritedTraits(
    normalizeDescriptionTraits(description?.traits, reproductiveGroup),
    inheritedTraits,
  );
  const recordedTraits = {
    ...manualRecordedTraits,
    ...inheritedTraits,
  };
  const effectiveDraftTraits = {
    ...draftTraits,
    ...inheritedTraits,
  };
  const traitCount = Object.keys(recordedTraits).length;
  const manualDraftTraitCount = Object.values(draftTraits).filter((value) =>
    Array.isArray(value) ? value.length > 0 : !!value,
  ).length;
  const effectiveDraftTraitCount = Object.keys(effectiveDraftTraits).length;
  const inheritedTraitCount = Object.keys(inheritedTraits).length;
  const hasDescriptionContent = traitCount > 0;
  const canSaveDescription = manualDraftTraitCount > 0 || Boolean(description);

  function openEditor() {
    setDraftTraits({ ...manualRecordedTraits });
    setBaseRevision(Number(description?.revision || 0));
    setSaveError("");
    setActiveSection("habit");
    setEditorOpen(true);
  }

  function updateTrait(key, value) {
    if (hasOwnTrait(inheritedTraits, key)) return;

    setSaveError("");
    setDraftTraits((current) => {
      const next = { ...current };

      const hasValue = Array.isArray(value) ? value.length > 0 : !!value;

      if (hasValue) next[key] = value;
      else delete next[key];

      if (key === "leafComplexity" && value !== "compound") {
        delete next.compoundType;
      }

      if (key === "flowerPresentation" && value !== "inflorescence") {
        delete next.inflorescenceType;
      }

      if (key === "flowerSexuality") {
        if (value !== "unisexual") delete next.unisexualSystem;
        if (value !== "mixed") delete next.mixedSexualSystem;
      }

      if (key === "petioleForm" && value !== "pulvinate") {
        delete next.petiolePulvinusPosition;
      }

      if (key === "perianthType") {
        if (!["sepals-only", "sepals-petals"].includes(value)) {
          delete next.sepalNumber;
        }

        if (!["petals-only", "sepals-petals"].includes(value)) {
          delete next.petalNumber;
        }

        if (value !== "tepals") {
          delete next.tepalNumber;
        }
      }

      if (["upperLeafSurface", "lowerLeafSurface"].includes(key)) {
        delete next.leafSurface;
      }

      if (key === "glands" && value === "absent") {
        delete next.glandPosition;
      }

      return next;
    });
  }

  async function saveDescription() {
    if (!auth.currentUser) {
      setSaveError("You must be signed in to edit a species description.");
      return;
    }

    const cleanedTraits = Object.fromEntries(
      Object.entries(draftTraits).filter(([, value]) =>
        Array.isArray(value) ? value.length > 0 : !!value,
      ),
    );

    if (Object.keys(cleanedTraits).length === 0 && !description) {
      setSaveError(
        inheritedTraitCount > 0
          ? "The inherited taxonomy traits are already active. Select at least one species-specific characteristic before saving."
          : "Select at least one identifying characteristic.",
      );
      return;
    }

    setSaving(true);
    setSaveError("");

    try {
      const functions = getFunctions(undefined, "southamerica-east1");
      const savePlantDescription = httpsCallable(
        functions,
        "savePlantSpeciesDescription",
      );

      await savePlantDescription({
        scientificName,
        traits: cleanedTraits,
        baseRevision,
      });

      setEditorOpen(false);
    } catch (error) {
      console.error("Failed to save plant description:", error);

      if (error?.code === "functions/aborted") {
        setSaveError(
          "Someone updated this description while you were editing. Close and reopen it to review the newest version.",
        );
      } else if (error?.code === "functions/permission-denied") {
        setSaveError(
          "Your account must be active and have accepted the current BioIndex policies before contributing.",
        );
      } else if (error?.code === "functions/failed-precondition") {
        setSaveError(
          error?.message ||
            "This species is not recognized as a plant in the BioIndex taxonomy.",
        );
      } else {
        setSaveError(
          error?.message ||
            "The description could not be saved. Please try again.",
        );
      }
    } finally {
      setSaving(false);
    }
  }

  const trigger = (
    <button
      type="button"
      onClick={openEditor}
      aria-label={`${hasDescriptionContent ? "Edit" : "Add"} shared plant description`}
      title={`${hasDescriptionContent ? "Edit" : "Add"} shared plant description`}
      style={detailButton ? { contain: "inline-size" } : undefined}
      className={
        detailButton
          ? `mt-4 block w-full min-w-0 max-w-full overflow-hidden rounded-xl border border-[#93CA87]/45 bg-[#FCFFF6] px-3 py-3 text-left shadow-sm transition hover:border-[#24C3BE] hover:bg-[#24C3BE]/5 ${className}`
          : `inline-flex min-h-7 min-w-7 items-center justify-center gap-1.5 rounded-md border border-[#24C3BE]/45 bg-white px-2 py-1.5 text-[11px] font-medium text-[#01767A] shadow-sm transition hover:border-[#24C3BE] hover:bg-[#24C3BE]/10 sm:px-3 sm:text-xs ${className}`
      }
    >
      {detailButton ? (
        <>
          <span className="flex w-full min-w-0 items-center gap-3">
            <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-[#374049] text-[#24C3BE]">
              <LeafIcon className="h-4 w-4" />
            </span>

            <span className="min-w-0 flex-1">
              <span className="block text-xs font-semibold text-[#374049]">
                Species description
              </span>

              <span className="mt-0.5 block truncate text-[10px] text-[#374049]/55 sm:text-[11px]">
                {loading
                  ? "Loading shared identification guide..."
                  : hasDescriptionContent
                    ? "Shared species-level identification"
                    : "Start the shared identification guide"}
              </span>
            </span>

            <span className="shrink-0 text-[10px] font-semibold uppercase tracking-wide text-[#01767A]">
              {hasDescriptionContent ? "Edit" : "Add"}

              <span aria-hidden="true" className="ml-1 text-base leading-none">
                ›
              </span>
            </span>
          </span>

          {!loading && traitCount > 0 && (
            <span className="mt-3 block min-w-0 max-w-full border-t border-[#93CA87]/30 pt-3">
              {DESCRIPTION_DISPLAY_GROUPS.map((group, groupIndex) => {
                const populatedFields = group.fields
                  .map((key) => FIELD_LOOKUP[key])
                  .filter((field) => field && recordedTraits[field.key]);

                if (populatedFields.length === 0) return null;

                return (
                  <span
                    key={group.id}
                    className={`block ${
                      groupIndex > 0
                        ? "mt-3 border-t border-[#93CA87]/20 pt-3"
                        : ""
                    }`}
                  >
                    <span className="mb-1.5 block text-[9px] font-bold uppercase tracking-[0.14em] text-[#01767A]/70 sm:text-[10px]">
                      {group.label}
                    </span>

                    <span className="flex min-w-0 max-w-full flex-wrap gap-1.5">
                      {populatedFields.map((field) => {
                        const key = field.key;
                        const value = recordedTraits[key];
                        const label = traitLabel(key, value);
                        const displayLabel = displayTraitLabel(key, value);
                        const help = getOptionHelp(field, value, label);
                        const inherited = hasOwnTrait(inheritedTraits, key);

                        return (
                          <span
                            key={key}
                            title={`${field.label}: ${help}${
                              inherited
                                ? ` Inherited from ${
                                    traitInheritance?.traitSources?.[key]
                                      ?.sourceName ||
                                    traitInheritance?.sourceName
                                  }.`
                                : ""
                            }`}
                            className={`inline-flex max-w-full flex-none items-center gap-1.5 rounded-lg border px-2 py-1.5 text-[10px] font-medium text-[#374049] shadow-sm sm:text-[11px] ${
                              inherited
                                ? "border-[#24C3BE]/50 bg-[#24C3BE]/10"
                                : "border-[#93CA87]/40 bg-white"
                            }`}
                          >
                            {field.icon !== false && (
                              <span className="flex shrink-0 items-center gap-0.5 text-[#01767A]">
                                {(Array.isArray(value) ? value : [value]).map(
                                  (traitValue) => (
                                    <TraitVisual
                                      key={traitValue}
                                      field={field}
                                      value={traitValue}
                                      className="h-4 w-4"
                                    />
                                  ),
                                )}
                              </span>
                            )}

                            <span className="min-w-0 break-words text-left leading-tight">
                              {displayLabel}
                            </span>
                          </span>
                        );
                      })}
                    </span>
                  </span>
                );
              })}
            </span>
          )}
        </>
      ) : (
        <>
          <LeafIcon className="h-3.5 w-3.5" />

          <span className="hidden sm:inline">Description</span>

          {hasDescriptionContent && (
            <span
              aria-label="Description available"
              className="h-1.5 w-1.5 rounded-full bg-[#24C3BE]"
            />
          )}
        </>
      )}
    </button>
  );

  return (
    <>
      {buttonOnly || detailButton ? (
        trigger
      ) : (
        <section
          className={`mt-4 overflow-hidden rounded-2xl border border-[#93CA87]/40 bg-white shadow-sm ${className}`}
        >
          <div className="flex flex-col gap-3 border-b border-[#93CA87]/30 bg-[#FCFFF6] px-4 py-4 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex min-w-0 items-center gap-3">
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border border-[#93CA87]/45 bg-white text-[#01767A] shadow-sm">
                <LeafIcon />
              </div>

              <div className="min-w-0">
                <div className="text-[10px] font-semibold uppercase tracking-[0.16em] text-[#01767A]">
                  Shared identification guide
                </div>
                <h4 className="truncate text-sm font-semibold text-[#374049] sm:text-base">
                  Plant description
                </h4>
              </div>
            </div>

            <button
              type="button"
              onClick={openEditor}
              className="inline-flex shrink-0 items-center justify-center gap-2 rounded-xl bg-[#374049] px-3.5 py-2 text-xs font-semibold text-white transition hover:bg-[#01767A]"
            >
              <LeafIcon className="h-3.5 w-3.5" />
              {hasDescriptionContent ? "Edit description" : "Add description"}
            </button>
          </div>

          <div className="px-4 py-4 sm:px-5">
            {loading && (
              <div className="flex items-center gap-3 py-2 text-xs text-[#374049]/55">
                <span className="h-4 w-4 animate-spin rounded-full border-2 border-[#93CA87] border-t-[#24C3BE]" />
                Loading shared characteristics...
              </div>
            )}

            {!loading && loadError && (
              <p className="text-xs text-red-600">{loadError}</p>
            )}

            {!loading && !loadError && traitCount === 0 && (
              <p className="text-sm leading-relaxed text-[#374049]/60">
                No identification characteristics have been recorded yet. Any
                BioIndex contributor can start this structured description.
              </p>
            )}

            {!loading && !loadError && traitCount > 0 && (
              <div className="space-y-4">
                {traitInheritance && inheritedTraitCount > 0 && (
                  <div className="inline-flex max-w-full items-center gap-2 rounded-xl border border-[#24C3BE]/45 bg-[#24C3BE]/10 px-3 py-2 text-[10px] leading-relaxed text-[#374049]/70 sm:text-xs">
                    <span
                      aria-hidden="true"
                      className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-white font-bold text-[#01767A]"
                    >
                      ◆
                    </span>
                    <span>
                      Inherited taxonomy traits from{" "}
                      <strong className="text-[#01767A]">
                        {traitInheritance.sourceName}
                      </strong>
                    </span>
                  </div>
                )}

                {visibleDescriptionSections.map((section) => {
                  const sectionTraits = section.fields.filter(
                    (field) => recordedTraits[field.key],
                  );

                  if (sectionTraits.length === 0) return null;

                  return (
                    <div key={section.id}>
                      <div className="mb-2 text-[10px] font-semibold uppercase tracking-[0.14em] text-[#374049]/50">
                        {section.label}
                      </div>

                      <div className="flex w-full min-w-0 max-w-full flex-wrap gap-2">
                        {sectionTraits.map((field) => {
                          const inherited = hasOwnTrait(
                            inheritedTraits,
                            field.key,
                          );

                          return (
                            <div
                              key={field.key}
                              className={`min-w-0 max-w-full break-words rounded-xl border px-3 py-2 text-xs text-[#374049] ${
                                inherited
                                  ? "border-[#24C3BE]/50 bg-[#24C3BE]/10"
                                  : "border-[#93CA87]/40 bg-[#FCFFF6]"
                              }`}
                            >
                              <span className="font-semibold">
                                {field.label}
                              </span>
                              <span className="mx-1.5 text-[#24C3BE]">•</span>
                              {traitLabel(field.key, recordedTraits[field.key])}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}

                <div className="border-t border-[#93CA87]/25 pt-3 text-[10px] text-[#374049]/45">
                  {traitCount}{" "}
                  {traitCount === 1 ? "characteristic" : "characteristics"}
                  {description?.updatedByName && (
                    <> · Last edited by {description.updatedByName}</>
                  )}
                  {formatUpdatedAt(description?.updatedAt) && (
                    <> · {formatUpdatedAt(description.updatedAt)}</>
                  )}
                </div>
              </div>
            )}
          </div>
        </section>
      )}

      {editorOpen && (
        <div
          className="fixed inset-0 z-[130] flex items-stretch justify-center bg-[#374049]/85 p-0 backdrop-blur-[2px] sm:items-center sm:p-5"
          onClick={() => {
            if (!saving) setEditorOpen(false);
          }}
        >
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="plant-description-title"
            className="flex h-full w-full max-w-5xl flex-col overflow-hidden bg-white shadow-2xl sm:h-auto sm:max-h-[92vh] sm:rounded-[2rem] sm:border sm:border-[#93CA87]/50"
            onClick={(event) => event.stopPropagation()}
          >
            <header className="shrink-0 border-b border-[#93CA87]/35 bg-[#FCFFF6] px-4 py-4 sm:px-6 sm:py-5">
              <div className="flex items-start justify-between gap-4">
                <div className="flex min-w-0 items-center gap-3">
                  <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl border border-[#93CA87]/50 bg-white text-[#01767A] shadow-sm">
                    <LeafIcon className="h-6 w-6" />
                  </div>

                  <div className="min-w-0">
                    <div className="text-[10px] font-semibold uppercase tracking-[0.17em] text-[#01767A]">
                      Collaborative plant description
                    </div>
                    <h2
                      id="plant-description-title"
                      className="truncate text-2xl italic leading-tight text-[#374049] sm:text-3xl"
                      style={{ fontFamily: "Cormorant Garamond" }}
                    >
                      {scientificName}
                    </h2>
                  </div>
                </div>

                <button
                  type="button"
                  onClick={() => setEditorOpen(false)}
                  disabled={saving}
                  aria-label="Close plant description"
                  className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl border border-[#93CA87]/45 bg-white text-xl text-[#374049] transition hover:border-[#24C3BE] hover:bg-[#24C3BE]/10 disabled:opacity-50"
                >
                  ×
                </button>
              </div>

              <p className="mt-3 max-w-3xl text-xs leading-relaxed text-[#374049]/60 sm:text-sm">
                Choose only characteristics that reliably describe this species.
                Changes to plant descriptions apply to the species across
                BioIndex, not just to your own photo.
              </p>

              {traitInheritance && (
                <div className="mt-3 inline-flex max-w-full items-center gap-2 rounded-xl border border-[#24C3BE]/45 bg-white px-3 py-2 text-[10px] leading-relaxed text-[#374049]/70 sm:text-xs">
                  <span
                    aria-hidden="true"
                    className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-[#24C3BE]/15 font-bold text-[#01767A]"
                  >
                    ◆
                  </span>
                  <span>
                    Taxonomy-derived traits inherited from{" "}
                    <strong className="text-[#01767A]">
                      {traitInheritance.sourceName}
                    </strong>{" "}
                    are locked to prevent contradictory species records.
                  </span>
                </div>
              )}
            </header>

            <nav
              aria-label="Plant description sections"
              className="shrink-0 overflow-x-auto border-b border-[#93CA87]/30 bg-white px-3 py-2 sm:px-6"
            >
              <div className="flex min-w-max gap-1.5">
                {visibleDescriptionSections.map((section) => {
                  const selectedCount = section.fields.filter(
                    (field) => effectiveDraftTraits[field.key],
                  ).length;

                  return (
                    <button
                      key={section.id}
                      type="button"
                      onClick={() => setActiveSection(section.id)}
                      className={`rounded-xl px-3 py-2 text-xs font-semibold transition ${
                        activeSection === section.id
                          ? "bg-[#374049] text-white shadow-sm"
                          : "text-[#374049]/65 hover:bg-[#24C3BE]/10 hover:text-[#01767A]"
                      }`}
                    >
                      {section.shortLabel}
                      {selectedCount > 0 && (
                        <span
                          className={`ml-2 rounded-full px-1.5 py-0.5 text-[9px] ${
                            activeSection === section.id
                              ? "bg-[#24C3BE] text-white"
                              : "bg-[#93CA87]/25 text-[#01767A]"
                          }`}
                        >
                          {selectedCount}
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
            </nav>

            <div
              ref={editorContentRef}
              className="min-h-0 flex-1 overflow-y-auto bg-[#f4f6f8] px-3 py-4 sm:px-6 sm:py-6"
            >
              <div className="mx-auto max-w-4xl space-y-3">
                {visibleDescriptionSections.find(
                  (section) => section.id === activeSection,
                )
                  ?.fields.filter(
                    (field) =>
                      !field.showWhen || field.showWhen(effectiveDraftTraits),
                  )
                  .map((field) => (
                    <TraitField
                      key={field.key}
                      field={field}
                      value={effectiveDraftTraits[field.key] || ""}
                      onChange={(value) => updateTrait(field.key, value)}
                      inheritedSource={
                        hasOwnTrait(inheritedTraits, field.key)
                          ? traitInheritance?.traitSources?.[field.key]
                              ?.sourceName || traitInheritance?.sourceName
                          : ""
                      }
                      copyFromValue={
                        field.copyFrom
                          ? effectiveDraftTraits[field.copyFrom] || ""
                          : ""
                      }
                      onCopyFrom={
                        field.copyFrom
                          ? (copyValue) =>
                              updateTrait(
                                field.key,
                                Array.isArray(copyValue)
                                  ? [...copyValue]
                                  : copyValue,
                              )
                          : undefined
                      }
                    />
                  ))}
              </div>
            </div>

            <footer className="shrink-0 border-t border-[#93CA87]/35 bg-white px-4 py-3 sm:px-6 sm:py-4">
              {saveError && (
                <div
                  role="alert"
                  className="mb-3 rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-xs leading-relaxed text-red-700"
                >
                  {saveError}
                </div>
              )}

              <div className="flex flex-col-reverse gap-2 sm:flex-row sm:items-center sm:justify-between">
                <p className="text-center text-[10px] text-[#374049]/50 sm:text-left">
                  {effectiveDraftTraitCount}{" "}
                  {effectiveDraftTraitCount === 1
                    ? "characteristic"
                    : "characteristics"}{" "}
                  selected
                  {inheritedTraitCount > 0 && (
                    <> · {inheritedTraitCount} inherited from taxonomy</>
                  )}{" "}
                  · Changes apply to {scientificName} across BioIndex, not just
                  your photo
                </p>

                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => setEditorOpen(false)}
                    disabled={saving}
                    className="flex-1 rounded-xl border border-[#93CA87]/45 bg-white px-4 py-2.5 text-sm font-semibold text-[#374049] transition hover:bg-slate-50 disabled:opacity-50 sm:flex-none"
                  >
                    Cancel
                  </button>

                  <button
                    type="button"
                    onClick={saveDescription}
                    disabled={saving || !canSaveDescription}
                    className="flex-1 rounded-xl bg-[#24C3BE] px-5 py-2.5 text-sm font-semibold text-white transition hover:bg-[#01767A] disabled:cursor-not-allowed disabled:bg-slate-300 sm:flex-none"
                  >
                    {saving ? "Saving..." : "Save shared description"}
                  </button>
                </div>
              </div>
            </footer>
          </div>
        </div>
      )}
    </>
  );
}
